import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AutoStatusToast from '../components/common/AutoStatusToast';
import {
  createTrack,
  deleteTracksForGrade,
  getGrades,
  getTracks,
  saveTracksCache,
} from '../services/gradeTrackService';
import {
  getGradeTrackProgramPreferences,
  getGradeTrackSections,
  loadGradeTrackProgramPreferencesCache,
  loadHoursAdjustSettingsCacheFirst,
  PageSectionLayout,
  PageSectionTrackColumn,
  saveGradeTrackProgramPreferences,
  saveGradeTrackSections,
  type GradeTrackProgramPreferences,
} from '../services/sectionLayoutService';
import {
  deleteTrackProgramDataForGradeTrackNames,
  renameTrackProgramDataForGradeTrackName,
} from '../services/programScopedContentService';
import NumericInput from '../components/common/NumericInput';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useClassSettingsModal } from '../contexts/ClassSettingsModalContext';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { Grade, Track } from '../types/domain';
import AiPresetImportModal from '../components/aiPreset/AiPresetImportModal';
import AiPresetOpenButton from '../components/aiPreset/AiPresetOpenButton';
import { gradeNumbersFromClassName, guessSchoolYear, type AiTrackRow } from '../utils/aiPresetExtract';
import { DEFAULT_SUBJECT_PRESETS } from '../constants/defaultSubjectPresets';
import { useTeamIdForLeaderImport } from '../hooks/useTeamIdForLeaderImport';
import GradeTrackLeaderSectionImportModal from '../components/gradeTrack/GradeTrackLeaderSectionImportModal';
import TrackHoursTermScopeToggle from '../components/gradeTrack/TrackHoursTermScopeToggle';
import TrackLearningPublisherSelect from '../components/gradeTrack/TrackLearningPublisherSelect';
import TrackLearningBatchGenerateConfirmModal from '../components/gradeTrack/TrackLearningBatchGenerateConfirmModal';
import TrackLearningBatchGenerateResultModal from '../components/gradeTrack/TrackLearningBatchGenerateResultModal';
import type { GradeTrackLeaderImportLocalPayload } from '../components/gradeTrack/GradeTrackLeaderSectionImportModal';
import { resolveLeaderPreviewTrackMeta } from '../services/gradeTrackTeamLeaderImportService';
import { loadTrackAliasOrderMap, saveTrackAliasOrderMap } from '../utils/trackAliasOrderStorage';
import { saveTrackOrderForGrade } from '../utils/trackOrderForGrade';
import {
  loadTrackProgramModeMap,
  type TrackProgramMode,
} from '../utils/trackProgramModeStorage';
import { loadTrackProgramSyncRepMap } from '../utils/trackProgramSyncRepStorage';
import { reseedGradeTrackProgramPrefsLocalStores } from '../utils/gradeTrackProgramPrefsLocalSeed';
import {
  ANNUAL_PREREQUISITE_REFRESH_EVENT,
  emitAnnualPrerequisiteRefresh,
  type AnnualPrerequisiteRefreshDetail,
} from '../utils/annualPrerequisiteRefresh';
import { TRACK_PROGRAM_SPLIT_CHANGE_EVENT } from '../utils/trackProgramSplitStorage';
import { localStorageSetItemWithQuotaRecovery } from '../utils/localStorageQuota';
import { resolveCalendarLinkedAcademicYear } from '../utils/calendarSectionAcademicYear';
import {
  generateProgramLearningFromSelectedPublishers,
  normalizeSchoolGradeNumbers,
  parseTrackLearningSourceSelection,
  type ProgramLearningBatchGenerateResult,
  type ProgramLearningBatchGenerateTarget,
} from '../utils/programLearningBatchGenerate';
import {
  getTrackHoursAdjustSettings,
  type TrackHoursAdjustTrackSettings,
  type TrackHoursTermScope,
} from '../utils/trackHoursTermScopeStorage';

type TrackCategory = 'regular' | 'other';

const CLASS_SECTION_ID_PREFIX = 'grade:';

type GradeTrackSectionClipboardColumn = PageSectionTrackColumn & {
  programMode: TrackProgramMode;
  syncRepAlias?: string;
};

type GradeTrackSectionClipboard = {
  sourceSectionId: string;
  sourceLabel: string;
  columns: GradeTrackSectionClipboardColumn[];
};

function parseClassName(raw: string): { title: string; grades: number[] } {
  const [base, rest] = raw.split('::');
  if (!rest) return { title: raw, grades: [] };
  const nums = rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
  return { title: base, grades: nums };
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function sectionIdForGrade(gradeId: string): string {
  return `${CLASS_SECTION_ID_PREFIX}${gradeId}`;
}

/** 클래스 단독 고정 섹션: 섹션의 원본은 항상 자기 클래스 */
function sectionSourceGradeId(
  section: { id: string; gradeIds: string[] },
  sectionSourceGradeIdBySection: Record<string, string>
): string {
  if (section.gradeIds.length === 0) return '';
  return (
    sectionSourceGradeIdBySection[section.id] ?? section.gradeIds[0] ?? ''
  ).trim();
}

function dedupeStrings(ids: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** DB unique (grade_id, name, description) 충돌 방지 — 순서 유지·빈 값 제거·중복 제거 */
function uniqueNonEmptyAliases(aliases: string[], fallbackName?: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of aliases) {
    const a = raw.trim();
    if (!a || seen.has(a)) continue;
    seen.add(a);
    out.push(a);
  }
  const fallback = fallbackName?.trim();
  if (out.length === 0 && fallback) return [fallback];
  return out;
}

function TrackAliasDefaultBadge({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <span
      className="cp-class-representative-badge track-alias-default-badge"
      title="와일드카드(괄호)로 입력된 시간표는 이 기본 시간표명으로 시수 집계·프로그램 구성됩니다"
    >
      기본
    </span>
  );
}

function sanitizeWildcardChar(raw: string): string {
  const text = raw.trim();
  const ch = text.match(/[가-힣]/)?.[0] ?? '';
  return ch;
}

/** 와일드카드 기본값: 편제명 첫 글자 */
function defaultWildcardFromName(name: string): string {
  return sanitizeWildcardChar(Array.from(name.trim())[0] ?? '');
}

function sameAliasList(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** draftEdits 항목이 서버 표시값과 동일하면 스냅샷에서 제외(클릭만으로 dirty 되는 것 방지) */
function isNoOpDraftEdit(
  draft: { name: string; aliases: string[]; wildcardChar?: string },
  serverName: string,
  serverAliases: string[],
  serverWildcard: string
): boolean {
  if (draft.name.trim() !== serverName.trim()) return false;
  if (sanitizeWildcardChar(draft.wildcardChar ?? '') !== sanitizeWildcardChar(serverWildcard)) {
    return false;
  }
  const draftAliases = draft.aliases.map((a) => a.trim()).filter((a) => a.length > 0);
  const baseAliases = serverAliases.map((a) => a.trim()).filter((a) => a.length > 0);
  return sameAliasList(draftAliases, baseAliases);
}

function TrackWildcardInput({
  value,
  duplicate,
  onChange,
}: {
  value: string;
  duplicate: boolean;
  onChange: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const composingRef = useRef(false);

  useEffect(() => {
    if (!composingRef.current) setDraft(value);
  }, [value]);

  const commit = (raw: string) => {
    const next = sanitizeWildcardChar(raw);
    setDraft(next);
    /** 클릭 후 blur만 해도 onChange('')가 나가 draftEdits가 생기는 것 방지 */
    if (next === sanitizeWildcardChar(value)) return;
    onChange(next);
  };

  const handleDraftChange = (raw: string) => {
    setDraft(raw);
    if (composingRef.current) return;
    const next = sanitizeWildcardChar(raw);
    if (next || raw === '') commit(raw);
  };

  return (
    <div
      className={'track-col-wildcard' + (duplicate ? ' is-duplicate' : '')}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="track-col-wildcard-label">(</span>
      <input
        className="track-col-wildcard-input"
        value={draft}
        onChange={(e) => handleDraftChange(e.target.value)}
        onCompositionStart={() => {
          composingRef.current = true;
        }}
        onCompositionEnd={(e) => {
          composingRef.current = false;
          commit(e.currentTarget.value);
        }}
        onBlur={(e) => {
          if (!composingRef.current) commit(e.currentTarget.value);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        inputMode="text"
        placeholder=" "
        aria-label="편제 와일드카드 한글자"
      />
      <span className="track-col-wildcard-label">)</span>
    </div>
  );
}

/** 동일 편제명 컬럼이 여러 번 쌓인 경우 하나로 합침 (입력 배열의 첫 등장 순서 유지) */
function mergeSectionTrackColumns(columns: PageSectionTrackColumn[]): PageSectionTrackColumn[] {
  const byName = new Map<string, PageSectionTrackColumn>();
  const order: string[] = [];
  for (const col of columns) {
    const name = col.name.trim();
    if (!name) continue;
    const aliases = uniqueNonEmptyAliases(col.aliases, name);
    const existing = byName.get(name);
    if (!existing) {
      byName.set(name, {
        ...col,
        name,
        aliases,
        wildcardChar: sanitizeWildcardChar(col.wildcardChar ?? '') || undefined,
      });
      order.push(name);
      continue;
    }
    byName.set(name, {
      ...existing,
      aliases: uniqueNonEmptyAliases([...existing.aliases, ...aliases], name),
      criteriaHours: col.criteriaHours ?? existing.criteriaHours,
      trackCategory: existing.trackCategory ?? col.trackCategory,
      id: existing.id ?? col.id,
      wildcardChar: sanitizeWildcardChar(existing.wildcardChar ?? col.wildcardChar ?? '') || undefined,
    });
  }
  return order.map((name) => byName.get(name)!);
}

function mergeTrackNameOrder(preferred: string[], allNames: string[]): string[] {
  const allSet = new Set(allNames);
  const existing = preferred.map((n) => n.trim()).filter((n) => n.length > 0 && allSet.has(n));
  const seen = new Set(existing);
  const additions = allNames.filter((n) => !seen.has(n));
  return [...existing, ...additions];
}

function orderedTrackNamesForSection(
  section: TrackSection,
  trackOrderByGrade: Record<string, string[]>,
  workspaceId: string,
  sourceGradeId: string
): string[] {
  const fromLayout = (section.trackColumns ?? [])
    .map((c) => c.name.trim())
    .filter((n) => n.length > 0);
  if (fromLayout.length > 0) return fromLayout;
  const fromState = trackOrderByGrade[sourceGradeId];
  if (fromState?.length) return fromState;
  if (typeof window !== 'undefined' && workspaceId && sourceGradeId) {
    try {
      const raw = window.localStorage.getItem(`cp.trackOrder.${workspaceId}.${sourceGradeId}`);
      if (raw) {
        const stored = JSON.parse(raw) as unknown;
        if (Array.isArray(stored)) {
          return stored
            .filter((n): n is string => typeof n === 'string')
            .map((n) => n.trim())
            .filter((n) => n.length > 0);
        }
      }
    } catch {
      // ignore
    }
  }
  return [];
}

function buildSectionColumnIdsFromNames(
  names: string[],
  sectionTracks: Track[],
  pendingForSection: Array<{ p: { id: string; name: string } }>,
  deletedTrackIds?: Set<string>
): string[] {
  const ids: string[] = [];
  const seenIds = new Set<string>();
  const seenNames = new Set<string>();
  const deleted = deletedTrackIds ?? new Set<string>();

  for (const name of names) {
    const n = name.trim();
    if (!n || seenNames.has(n)) continue;
    const group = sectionTracks.filter((t) => t.name === n);
    if (group.length > 0) {
      const id = group[0].id;
      if (deleted.has(id) || seenIds.has(id)) continue;
      seenIds.add(id);
      seenNames.add(n);
      ids.push(id);
      continue;
    }
    const pending = pendingForSection.find(({ p }) => p.name.trim() === n);
    if (pending && !seenIds.has(pending.p.id)) {
      seenIds.add(pending.p.id);
      seenNames.add(n);
      ids.push(pending.p.id);
    }
  }

  for (const { p } of pendingForSection) {
    const n = p.name.trim();
    if (!n || seenIds.has(p.id) || seenNames.has(n)) continue;
    seenIds.add(p.id);
    ids.push(p.id);
  }

  for (const t of sectionTracks) {
    if (deleted.has(t.id) || seenIds.has(t.id)) continue;
    const n = (t.name ?? '').trim();
    if (!n || seenNames.has(n)) continue;
    seenIds.add(t.id);
    ids.push(t.id);
  }

  return dedupeStrings(ids);
}

function columnsFromTracksForGrade(
  tracks: Track[],
  gradeId: string,
  nameOrder?: string[]
): PageSectionTrackColumn[] {
  const byName = new Map<string, Track[]>();
  tracks
    .filter((t) => (t.grade_id ?? '').trim() === gradeId)
    .forEach((track) => {
      const name = track.name.trim();
      if (!name) return;
      byName.set(name, [...(byName.get(name) ?? []), track]);
    });

  const allNames = Array.from(byName.keys());
  const orderedNames =
    nameOrder && nameOrder.length > 0 ? mergeTrackNameOrder(nameOrder, allNames) : allNames;

  return orderedNames.map((name) => {
    const group = byName.get(name)!;
    return {
      id: group[0]?.id,
      name,
      aliases: uniqueNonEmptyAliases(
        group.map((row) => (row.description ?? '').trim()).filter(Boolean),
        name
      ),
      criteriaHours: Math.min(999, Math.max(0, group[0]?.criteria_hours ?? 0)),
      trackCategory: group.some((row) => row.track_category === 'other') ? 'other' : 'regular',
    };
  });
}

function normalizeClassStandaloneSections(
  grades: Grade[],
  savedSections: TrackSection[],
  trackRows: Track[]
): TrackSection[] {
  return grades.map((grade) => {
    const savedForGrade = savedSections.find((section) => section.gradeIds.includes(grade.id));
    const savedColumns = mergeSectionTrackColumns(savedForGrade?.trackColumns ?? []);
    return {
      id: sectionIdForGrade(grade.id),
      gradeIds: [grade.id],
      sourceGradeId: grade.id,
      trackColumns:
        savedColumns.length > 0 ? savedColumns : columnsFromTracksForGrade(trackRows, grade.id),
    };
  });
}

/** 섹션 편제 컬럼 표시·드래그 순서 (항목은 track id 또는 pending uuid) */
function buildSectionDisplayColumnIds(params: {
  sectionTracks: Track[];
  orderedNames: string[];
  pendingForSection: Array<{ p: { id: string; name: string }; globalIndex: number }>;
  deletedTrackIds: Set<string>;
  savedOrder?: string[];
  layoutColumnNames?: string[];
}): string[] {
  const { sectionTracks, orderedNames, pendingForSection, deletedTrackIds, savedOrder, layoutColumnNames } =
    params;

  const effectiveNameOrder =
    layoutColumnNames && layoutColumnNames.length > 0
      ? mergeTrackNameOrder(
          layoutColumnNames,
          orderedNames.length > 0 ? orderedNames : sectionTracks.map((t) => t.name.trim()).filter(Boolean)
        )
      : orderedNames;

  const serverColumnIds: string[] = [];
  const serverNameSet = new Set<string>();
  for (const name of effectiveNameOrder) {
    const group = sectionTracks.filter((t) => t.name === name);
    if (group.length === 0) continue;
    if (deletedTrackIds.has(group[0].id)) continue;
    if (serverNameSet.has(name)) continue;
    serverNameSet.add(name);
    serverColumnIds.push(group[0].id);
  }

  const pendingColumnIds = pendingForSection
    .filter(({ p }) => !serverNameSet.has(p.name.trim()))
    .map(({ p }) => p.id);

  const baseIds = [...serverColumnIds, ...pendingColumnIds];
  const baseSet = new Set(baseIds);

  const resolveEntry = (entry: string): string | null => {
    if (baseSet.has(entry)) return entry;
    const byId = sectionTracks.find((t) => t.id === entry);
    if (byId && !deletedTrackIds.has(byId.id) && baseSet.has(byId.id)) return byId.id;
    const byNameGroup = sectionTracks.filter((t) => t.name === entry);
    if (byNameGroup.length > 0 && !deletedTrackIds.has(byNameGroup[0].id)) {
      const id = byNameGroup[0].id;
      return baseSet.has(id) ? id : null;
    }
    const pending = pendingForSection.find(({ p }) => p.id === entry || p.name.trim() === entry);
    return pending && baseSet.has(pending.p.id) ? pending.p.id : null;
  };

  if (!savedOrder?.length) return dedupeStrings(baseIds);

  const fromSaved = dedupeStrings(
    savedOrder.map(resolveEntry).filter((id): id is string => id != null)
  );
  const added = baseIds.filter((id) => !fromSaved.includes(id));
  return dedupeStrings([...fromSaved, ...added]);
}

function buildOrderedAliasesForTrack(
  gradeId: string,
  trackName: string,
  columnAliases: string[],
  trackAliasOrderByGrade: Record<string, Record<string, string[]>>
): string[] {
  const stored = trackAliasOrderByGrade[gradeId]?.[trackName] ?? [];
  const nonEmpty = columnAliases.map((a) => a.trim()).filter((a) => a.length > 0);
  const base = nonEmpty.length > 0 ? nonEmpty : [trackName.trim()].filter(Boolean);
  if (!stored.length) return base.length ? base : [trackName.trim() || ''];
  const ordered = [
    ...stored.map((a) => a.trim()).filter((a) => base.includes(a)),
    ...base.filter((a) => !stored.map((x) => x.trim()).includes(a)),
  ];
  return ordered.length ? ordered : base;
}

function effectiveSyncRepForColumn(
  gradeIds: string[],
  trackName: string,
  columnAliases: string[],
  trackAliasOrderByGrade: Record<string, Record<string, string[]>>,
  repByGrade: Record<string, Record<string, string>>
): string {
  if (!gradeIds.length) return '';
  const primary = gradeIds[0];
  const ordered = buildOrderedAliasesForTrack(primary, trackName, columnAliases, trackAliasOrderByGrade);
  if (ordered.length < 2) return ordered[0] ?? '';
  const rep = repByGrade[primary]?.[trackName] ?? '';
  if (rep && ordered.includes(rep)) return rep;
  return ordered[0] ?? '';
}

function consolidatedProgramMode(
  gradeIds: string[],
  trackName: string,
  modeByGrade: Record<string, Record<string, TrackProgramMode>>
): TrackProgramMode {
  if (!gradeIds.length || !trackName.trim()) return 'unified';
  const modes = gradeIds.map((gid) => modeByGrade[gid]?.[trackName] ?? 'unified');
  const first = modes[0];
  return modes.every((m) => m === first) ? first : 'unified';
}

function consolidatedTrackCategory(
  gradeIds: string[],
  trackName: string,
  categoryByGrade: Record<string, Record<string, TrackCategory>>
): TrackCategory {
  if (!gradeIds.length || !trackName.trim()) return 'regular';
  const values = gradeIds.map((gid) => categoryByGrade[gid]?.[trackName] ?? 'regular');
  const first = values[0];
  return values.every((v) => v === first) ? first : 'regular';
}

function consolidatedLearningPublisher(
  gradeIds: string[],
  trackName: string,
  publisherByGrade: Record<string, Record<string, string>>
): string {
  if (!gradeIds.length || !trackName.trim()) return '';
  for (const gradeId of gradeIds) {
    const publisher = (publisherByGrade[gradeId]?.[trackName] ?? '').trim();
    if (publisher) return publisher;
  }
  return '';
}

function consolidatedHoursTermScope(
  gradeIds: string[],
  trackName: string,
  hoursAdjustByGrade: Record<string, Record<string, TrackHoursAdjustTrackSettings>>
): TrackHoursTermScope {
  if (!gradeIds.length || !trackName.trim()) return 'split';
  const values = gradeIds.map(
    (gid) => hoursAdjustByGrade[gid]?.[trackName]?.termScope ?? 'split'
  );
  const first = values[0] ?? 'split';
  return values.every((v) => v === first) ? first : 'split';
}

function isUnifiedHoursAdjustTrack(
  trackName: string,
  hoursAdjustByGrade: Record<string, Record<string, TrackHoursAdjustTrackSettings>>
): boolean {
  const tn = trackName.trim();
  if (!tn) return false;
  return Object.values(hoursAdjustByGrade).some((map) => map?.[tn]?.termScope === 'unified');
}

function lowestGradeNumberFromClassName(raw: string): 1 | 2 | 3 | 4 | 5 | 6 {
  const nums = parseClassName(raw).grades;
  if (!nums.length) return 1;
  return Math.min(...nums) as 1 | 2 | 3 | 4 | 5 | 6;
}

function gradeNumbersFromGradeIds(gradeIds: string[], gradeRows: Grade[]): Array<1 | 2 | 3 | 4 | 5 | 6> {
  const nums: number[] = [];
  for (const id of gradeIds) {
    const grade = gradeRows.find((g) => g.id === id);
    nums.push(...parseClassName(grade?.name ?? '').grades);
  }
  const fromClass = normalizeSchoolGradeNumbers(nums);
  if (nums.some((n) => n >= 1 && n <= 6)) return fromClass;
  return [1, 2, 3, 4, 5, 6];
}

function getProgramModeLabel(mode: TrackProgramMode): string {
  if (mode === 'split') return '시간표별';
  if (mode === 'sync') return '동기화';
  return '편제별';
}

function getNextProgramMode(mode: TrackProgramMode): TrackProgramMode {
  if (mode === 'unified') return 'split';
  if (mode === 'split') return 'sync';
  return 'unified';
}

type TrackSection = PageSectionLayout;

interface GradeTrackPageProps {
  workspaceId: string;
  currentGradeId: string;
  grades: Grade[];
  onSwitchToClassIntegrated?: () => void;
}

/** 드래그 가능한 pending 컬럼 래퍼 (useSortable 적용) */
const SortablePendingColumn: React.FC<{ id: string; children: React.ReactNode }> = ({
  id,
  children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={'track-col' + (isDragging ? ' dragging' : '')}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
};
interface PendingTrackColumnProps {
  name: string;
  aliases: string[];
  criteriaHours: string;
  wildcardChar: string;
  wildcardDuplicate: boolean;
  onCriteriaHoursChange: (value: string) => void;
  onWildcardChange: (value: string) => void;
  onSaveDraft: (name: string, aliases: string[]) => void;
  onAliasesReorder: (aliases: string[]) => void;
  onRemove: () => void;
  onShowWarning: (message: string) => void;
  learningPublisher: string;
  onLearningPublisherChange: (publisher: string) => void;
  onPublisherTemplateSelect?: (templateName: string) => void;
  termScope: TrackHoursTermScope;
  onTermScopeChange: (scope: TrackHoursTermScope) => void;
  classTitle: string;
  learningGradeNumbers: Array<1 | 2 | 3 | 4 | 5 | 6>;
  workspaceId: string;
  programMode: TrackProgramMode;
  onProgramModeChange: (next: TrackProgramMode) => void;
  syncRepAlias: string;
  onSyncRepAliasChange: (alias: string) => void;
  trackCategory: TrackCategory;
  onTrackCategoryToggle: () => void;
  /** false면 바깥 track-col 없이 내용만 렌더 (SortablePendingColumn 안에서 사용) */
  wrapInTrackCol?: boolean;
  /** 신규 편제 생성 카드: 처음부터 편집 상태, 헤더 아이콘 숨김, 취소/등록 버튼 */
  createMode?: boolean;
  onCancelCreate?: () => void;
  /** 생성 모드: 편제명 입력 변화 알림(와일드카드 자동 채움용) */
  onDraftNameChange?: (name: string) => void;
}

interface SortableAliasInlineRowProps {
  id: string;
  alias: string;
  onAliasChange: (value: string) => void;
  onRemove?: () => void;
  removable?: boolean;
}

const SortableAliasInlineRow: React.FC<SortableAliasInlineRowProps> = ({
  id,
  alias,
  onAliasChange,
  onRemove,
  removable = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`track-alias-inline-row${isDragging ? ' is-sorting-drag' : ''}`}
    >
      <input
        className="track-alias-inline"
        value={alias}
        placeholder="시간표명"
        onChange={(e) => onAliasChange(e.target.value)}
      />
      <button
        type="button"
        className="track-alias-drag-handle-btn"
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="시간표명 순서 변경"
        title="드래그하여 순서 변경"
        {...attributes}
        {...listeners}
      >
        <span className="track-alias-drag-handle-icon" aria-hidden="true">
          ≡
        </span>
      </button>
      {onRemove ? (
        <button
          type="button"
          className="track-alias-remove-btn"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={removable ? onRemove : undefined}
          disabled={!removable}
          aria-label="시간표명 삭제"
          title="시간표명 삭제"
        >
          <img src="/Trash.svg" alt="삭제" className="track-icon" />
        </button>
      ) : null}
    </div>
  );
};

const PendingTrackColumn: React.FC<PendingTrackColumnProps> = ({
  name,
  aliases,
  criteriaHours,
  wildcardChar,
  wildcardDuplicate,
  onCriteriaHoursChange,
  onWildcardChange,
  onSaveDraft,
  onAliasesReorder,
  onRemove,
  onShowWarning,
  learningPublisher,
  onLearningPublisherChange,
  onPublisherTemplateSelect,
  termScope,
  onTermScopeChange,
  classTitle,
  learningGradeNumbers,
  workspaceId,
  programMode,
  onProgramModeChange,
  syncRepAlias,
  onSyncRepAliasChange,
  trackCategory,
  onTrackCategoryToggle,
  wrapInTrackCol = true,
  createMode = false,
  onCancelCreate,
  onDraftNameChange,
}) => {
  const displayAliases = aliases.length ? aliases : [name];
  const [editing, setEditing] = useState(createMode);
  const [draftName, setDraftName] = useState(name);
  const [draftAliases, setDraftAliases] = useState(displayAliases);
  const [draftAliasIds, setDraftAliasIds] = useState<string[]>(() =>
    displayAliases.map(() => generateId())
  );
  const aliasDragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  const handleCriteriaChange = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 3);
    onCriteriaHoursChange(digits);
  };
  const handleConfirm = () => {
    const nextName = draftName.trim();
    if (!nextName) return;
    const trimmed = draftAliases.map((a) => a.trim());
    if (!createMode && trimmed.filter((a) => a.length > 0).length === 0) {
      onShowWarning('시간표명은 최소 1개 이상 입력해야 합니다.');
      return;
    }
    const nonEmpty = trimmed.filter((a) => a.length > 0);
    onSaveDraft(nextName, createMode && nonEmpty.length === 0 ? [nextName] : nonEmpty);
    if (!createMode) setEditing(false);
  };

  const content = (
    <>
      <div className="track-col-header">
        <div className="track-header-top">
          <div className="track-header-actions" style={createMode ? { visibility: 'hidden' } : undefined}>
            <button
              type="button"
              className="track-icon-button"
              onClick={() => {
                setDraftName(name);
                setDraftAliases(displayAliases);
                setDraftAliasIds(displayAliases.map(() => generateId()));
                if (!editing && !sanitizeWildcardChar(wildcardChar)) {
                  const def = defaultWildcardFromName(name);
                  if (def) onWildcardChange(def);
                }
                setEditing((prev) => !prev);
              }}
            >
              <img src="/edit.svg" alt="편제 수정" className="track-icon" />
            </button>
            <button
              type="button"
              className="track-icon-button"
              onClick={onRemove}
              aria-label="삭제"
              title="삭제"
            >
              <img src="/Trash.svg" alt="삭제" className="track-icon" />
            </button>
          </div>
        </div>
        <div className="track-category-toggle-row">
          <button
            type="button"
            className={'track-category-toggle-badge' + (trackCategory === 'other' ? ' is-other' : '')}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onTrackCategoryToggle}
            aria-label={`편제 속성: ${trackCategory === 'other' ? '기타' : '정규'} (클릭 시 ${
              trackCategory === 'other' ? '정규' : '기타'
            }로 변경)`}
            title="클릭하여 편제 속성을 정규/기타로 변경"
          >
            {trackCategory === 'other' ? '기타' : '정규'}
          </button>
        </div>
        <div className="track-col-name">
          {editing ? (
            <input
              className="track-name-inline"
              value={draftName}
              placeholder={createMode ? '편제명' : undefined}
              autoFocus={createMode}
              onPointerDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                setDraftName(e.target.value);
                onDraftNameChange?.(e.target.value);
              }}
            />
          ) : (
            name
          )}
        </div>
      </div>
      <TrackWildcardInput
        value={wildcardChar}
        duplicate={wildcardDuplicate}
        onChange={onWildcardChange}
      />
      <div className="track-col-body">
        {editing
          ? (() => {
              const items = draftAliasIds;
              const handleAliasDragEnd = (event: DragEndEvent) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const oldIndex = draftAliasIds.indexOf(String(active.id));
                const newIndex = draftAliasIds.indexOf(String(over.id));
                if (oldIndex < 0 || newIndex < 0) return;
                setDraftAliasIds((prev) => arrayMove(prev, oldIndex, newIndex));
                setDraftAliases((prev) => {
                  const next = arrayMove(prev, oldIndex, newIndex);
                  onAliasesReorder(next);
                  return next;
                });
              };
              return (
                <DndContext
                  sensors={aliasDragSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleAliasDragEnd}
                >
                  <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    {draftAliasIds.map((aliasId, index) => (
                      <SortableAliasInlineRow
                        key={aliasId}
                        id={aliasId}
                        alias={draftAliases[index] ?? ''}
                        onAliasChange={(value) =>
                          setDraftAliases((prev) => {
                            const next = [...prev];
                            next[index] = value;
                            return next;
                          })
                        }
                        removable={draftAliases.length >= 2}
                        onRemove={() => {
                          setDraftAliases((prev) => prev.filter((_, i) => i !== index));
                          setDraftAliasIds((prev) => prev.filter((_, i) => i !== index));
                        }}
                      />
                    ))}
                  </SortableContext>
                  <button
                    type="button"
                    className="track-alias-add-btn"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      setDraftAliases((prev) => [...prev, '']);
                      setDraftAliasIds((prev) => [...prev, generateId()]);
                    }}
                    aria-label="시간표명 추가"
                    title="시간표명 추가"
                  >
                    + 시간표명
                  </button>
                </DndContext>
              );
            })()
          : displayAliases.map((a, i) =>
              programMode === 'sync' ? (
                <button
                  key={`${name}-${i}`}
                  type="button"
                  className={
                    'track-alias track-alias-sync-select' +
                    (a === syncRepAlias ? ' is-sync-rep' : ' is-sync-non-rep')
                  }
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => onSyncRepAliasChange(a)}
                  title={
                    a === syncRepAlias
                      ? '대표 시간표(프로그램 편집은 대표 시간표 메뉴에서만 가능하며 저장 시 다른 동기화 프로그램으로 복제됩니다)'
                      : '클릭하여 대표 시간표로 지정 (대표에서 저장한 내용이 다른 동기화 프로그램으로 복제됩니다)'
                  }
                >
                  <span className="track-alias-sync-select-label">{a || '\u00A0'}</span>
                  {a === syncRepAlias ? (
                    <span className="cp-class-representative-badge track-alias-rep-badge">대표</span>
                  ) : null}
                  <TrackAliasDefaultBadge
                    visible={i === 0 && !!sanitizeWildcardChar(wildcardChar)}
                  />
                </button>
              ) : (
                <div key={`${name}-${i}`} className="track-alias">
                  {a || '\u00A0'}
                  <TrackAliasDefaultBadge
                    visible={i === 0 && !!sanitizeWildcardChar(wildcardChar)}
                  />
                </div>
              )
            )}
        {displayAliases.length >= 2 && (
          <div className="track-split-mode-row">
            <span className="track-split-mode-title">프로그램</span>
            <span className="track-split-mode-title">생성 방법</span>
            <button
              type="button"
              className={`track-split-single-toggle-btn ${
                programMode !== 'unified' ? 'is-on' : 'is-off'
              } ${programMode === 'sync' ? 'is-sync' : ''}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onProgramModeChange(getNextProgramMode(programMode))}
              aria-label={`프로그램 생성 방법: ${getProgramModeLabel(programMode)} (클릭 시 ${
                getProgramModeLabel(getNextProgramMode(programMode))
              })`}
              title="클릭할 때마다 편제별 → 시간표별 → 동기화 순서로 전환"
            >
              {getProgramModeLabel(programMode)}
            </button>
          </div>
        )}
      </div>
      <div className="track-col-criteria">
        <span className="track-col-criteria-label">기준시수</span>
        <NumericInput
          className="track-col-criteria-input"
          min={0}
          max={999}
          maxLength={3}
          selectAllOnFocus
          value={criteriaHours}
          onChange={handleCriteriaChange}
        />
      </div>
      <TrackLearningPublisherSelect
        workspaceId={workspaceId}
        classTitle={classTitle}
        gradeNumbers={learningGradeNumbers}
        trackName={createMode ? draftName.trim() : name}
        value={learningPublisher}
        onChange={onLearningPublisherChange}
        onTemplateSelect={onPublisherTemplateSelect}
      />
      <TrackHoursTermScopeToggle value={termScope} onChange={onTermScopeChange} />
      {editing && (
        <div className="track-edit-footer">
          <button
            type="button"
            className="track-edit-cancel"
            onClick={() => (createMode ? onCancelCreate?.() : setEditing(false))}
          >
            {createMode ? '취소' : '닫기'}
          </button>
          <button
            type="button"
            className="track-edit-save"
            onClick={handleConfirm}
            disabled={createMode && !draftName.trim()}
          >
            {createMode ? '등록' : '확인'}
          </button>
        </div>
      )}
    </>
  );

  return wrapInTrackCol ? <div className="track-col">{content}</div> : content;
};

export interface TrackCreatePayload {
  name: string;
  aliases: string[];
  criteriaHours: string;
  wildcardChar: string;
  category: TrackCategory;
  termScope: TrackHoursTermScope;
  publisher: string;
}

/** 블럭 그리드 끝 '편제 추가' 생성 카드 — 편집 카드(PendingTrackColumn)를 생성 모드로 재사용 */
const TrackCreateCard: React.FC<{
  classTitle: string;
  learningGradeNumbers: Array<1 | 2 | 3 | 4 | 5 | 6>;
  workspaceId: string;
  /** 같은 섹션 기존 블럭들의 와일드카드 사용 횟수 */
  existingWildcardCounts: Map<string, number> | undefined;
  onCancel: () => void;
  onCreate: (payload: TrackCreatePayload) => void;
  onShowWarning: (message: string) => void;
}> = ({ classTitle, learningGradeNumbers, workspaceId, existingWildcardCounts, onCancel, onCreate, onShowWarning }) => {
  const [criteriaHours, setCriteriaHours] = useState('0');
  const [wildcardChar, setWildcardChar] = useState('');
  /** 사용자가 와일드카드를 직접 수정하면 편제명 자동 동기화 중단 */
  const [wildcardTouched, setWildcardTouched] = useState(false);
  const [category, setCategory] = useState<TrackCategory>('regular');
  const [termScope, setTermScope] = useState<TrackHoursTermScope>('split');
  const [publisher, setPublisher] = useState('');
  const wc = sanitizeWildcardChar(wildcardChar);
  return (
    <div className="track-col track-col-create" onPointerDown={(e) => e.stopPropagation()}>
      <PendingTrackColumn
        wrapInTrackCol={false}
        createMode
        onCancelCreate={onCancel}
        name=""
        aliases={['']}
        criteriaHours={criteriaHours}
        wildcardChar={wc}
        wildcardDuplicate={!!wc && (existingWildcardCounts?.get(wc) ?? 0) >= 1}
        onCriteriaHoursChange={setCriteriaHours}
        onWildcardChange={(v) => {
          setWildcardTouched(true);
          setWildcardChar(sanitizeWildcardChar(v));
        }}
        onDraftNameChange={(n) => {
          if (!wildcardTouched) setWildcardChar(defaultWildcardFromName(n));
        }}
        onSaveDraft={(name, aliases) =>
          onCreate({ name, aliases, criteriaHours, wildcardChar: wc, category, termScope, publisher })
        }
        onAliasesReorder={() => {}}
        onRemove={onCancel}
        onShowWarning={onShowWarning}
        learningPublisher={publisher}
        onLearningPublisherChange={setPublisher}
        termScope={termScope}
        onTermScopeChange={setTermScope}
        classTitle={classTitle}
        learningGradeNumbers={learningGradeNumbers}
        workspaceId={workspaceId}
        programMode="unified"
        onProgramModeChange={() => {}}
        syncRepAlias=""
        onSyncRepAliasChange={() => {}}
        trackCategory={category}
        onTrackCategoryToggle={() => setCategory((c) => (c === 'other' ? 'regular' : 'other'))}
      />
    </div>
  );
};

const GradeTrackPage: React.FC<GradeTrackPageProps> = ({
  workspaceId,
  currentGradeId,
  grades: _gradesFromNav,
}) => {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [sectionInputs, setSectionInputsRaw] = useState<Record<string, { name: string; alias: string }>>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const { toastState: saveToastState, showSaving, showSuccess, showError, showInfo, clearToast } =
    useAutoStatusToast(3000);
  const [trackOrderByGrade, setTrackOrderByGrade] = useState<Record<string, string[]>>({});
  /** 편제별 시간표명 순서(드래그 정렬) - gradeId 단위 localStorage */
  const [trackAliasOrderByGrade, setTrackAliasOrderByGradeRaw] = useState<
    Record<string, Record<string, string[]>>
  >({});
  /** 편제별 프로그램 생성 방식(unified/split/sync) - gradeId 단위 localStorage v2 */
  const [trackProgramModeByGrade, setTrackProgramModeByGradeRaw] = useState<
    Record<string, Record<string, TrackProgramMode>>
  >({});
  /** 동기화 모드일 때 편제명 → 대표 시간표명 */
  const [trackProgramSyncRepByGrade, setTrackProgramSyncRepByGradeRaw] = useState<
    Record<string, Record<string, string>>
  >({});
  const [trackCategoryByGrade, setTrackCategoryByGradeRaw] = useState<
    Record<string, Record<string, TrackCategory>>
  >({});
  const [learningPublisherByGrade, setLearningPublisherByGradeRaw] = useState<
    Record<string, Record<string, string>>
  >({});
  const [hoursAdjustByGrade, setHoursAdjustByGradeRaw] = useState<
    Record<string, Record<string, TrackHoursAdjustTrackSettings>>
  >({});
  const [batchLearningBusy, setBatchLearningBusy] = useState(false);
  const [batchLearningConfirmTargets, setBatchLearningConfirmTargets] = useState<
    ProgramLearningBatchGenerateTarget[] | null
  >(null);
  const [batchLearningResult, setBatchLearningResult] =
    useState<ProgramLearningBatchGenerateResult | null>(null);
  /** 삭제 확인 모달용: 확인 시 로컬 pendingTrackGroupDeletes에만 넣고, 저장 시 DB 반영 */
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState<{
    sectionId: string;
    group: Track[];
  } | null>(null);
  /** 저장 시 deleteTrackGroup 호출할 편제 그룹 목록 (로컬만 삭제 상태) */
  const [pendingTrackGroupDeletes, setPendingTrackGroupDeletesRaw] = useState<
    { sectionId: string; group: Track[] }[]
  >([]);
  /** 섹션에서 클래스가 빠진 학년 — 저장 시 해당 학년 편제 전체 삭제 */
  const [pendingOrphanGradeTrackWipes, setPendingOrphanGradeTrackWipesRaw] = useState<string[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [sections, setSectionsRaw] = useState<TrackSection[]>([]);
  /** 섹션 화면 원본으로 사용할 기준 클래스(신규 클래스 등록 시 과거 DB 데이터 자동 유입 방지) */
  const [sectionSourceGradeIdBySection, setSectionSourceGradeIdBySectionRaw] = useState<
    Record<string, string>
  >({});
  const teamIdForLeaderImport = useTeamIdForLeaderImport(workspaceId);
  const [leaderImportTargetSectionId, setLeaderImportTargetSectionId] = React.useState<string | null>(null);
  const [copiedSectionData, setCopiedSectionData] = useState<GradeTrackSectionClipboard | null>(null);
  const [pasteTargetSectionId, setPasteTargetSectionId] = useState<string | null>(null);
  const [resetSectionContext, setResetSectionContext] = useState<string | null>(null);
  /** '편제 추가' 생성 카드가 열린 섹션 id */
  const [creatingTrackSectionId, setCreatingTrackSectionId] = useState<string | null>(null);
  /** 기본 편제 예시 불러오기 확인 모달(기존 블럭 덮어쓰기·학년 선택) */
  const [presetLoadContext, setPresetLoadContext] = useState<{
    sectionId: string;
    gradeOptions: Array<1 | 2 | 3 | 4 | 5 | 6>;
    grade: 1 | 2 | 3 | 4 | 5 | 6 | null;
    existingNames: string[];
    keepUnmatched: boolean;
  } | null>(null);

  /** 저장 전 로컬에만 두는 신규 편제 (등록 시 같은 편제명이면 같은 블럭에 시간표명만 추가) */
  const [pendingAdds, setPendingAddsRaw] = useState<
    { sectionId: string; gradeIds: string[]; name: string; aliases: string[]; wildcardChar?: string; id: string }[]
  >([]);
  /** 섹션별 컬럼 순서 (서버 이름 + pending id 혼합, 드래그 시 갱신) */
  const [sectionColumnOrder, setSectionColumnOrderRaw] = useState<Record<string, string[]>>({});

  useEffect(() => {
    setPendingAddsRaw((prev) => {
      const need = prev.some((p) => !('id' in p) || !(p as { id?: string }).id);
      if (!need) return prev;
      return prev.map((p) =>
        'id' in p && (p as { id?: string }).id ? p : { ...p, id: generateId() }
      ) as typeof prev;
    });
  }, []);

  /** 컬럼별 편제명/시간표명 수정 초안 (저장 클릭 시 서버 대신 여기에 반영) */
  const [draftEdits, setDraftEditsRaw] = useState<
    Record<string, { name: string; aliases: string[]; wildcardChar?: string }>
  >({});
  /** 컬럼별 기준 시수 (track id 기준, 저장 시 서버 반영) */
  const [criteriaHoursByKey, setCriteriaHoursByKeyRaw] = useState<Record<string, string>>({});

  /**
   * dirty(저장 버튼 활성화) 판단은 스냅샷 문자열 비교에 더해 "사용자가 실제로 편집했는가"를
   * 별도로 추적한다. load()/저장 후 초기화/폐기 등 프로그램이 스스로 상태를 맞추는 동안에는
   * 아래 Raw 세터를 직접 호출해 이 카운터를 올리지 않고, 화면의 입력·버튼 등 사용자 조작이
   * 지나가는 세터(Raw가 아닌 원래 이름)만 호출 시 카운터를 올린다.
   * 기준 스냅샷을 다시 찍을 때는 "스냅샷을 찍기로 결정한 시점"의 카운터 값을 그대로 기준으로
   * 고정해 두므로, 그 이후(비동기 지연 중 포함)에 사용자가 편집하면 문자열이 우연히 같아져도
   * 항상 dirty로 유지된다.
   */
  const userEditEpochRef = useRef(0);
  const baselineEditEpochRef = useRef(0);
  const [, bumpDirtyProbe] = useState(0);
  const markUserEdit = useCallback(() => {
    userEditEpochRef.current += 1;
    bumpDirtyProbe((n) => n + 1);
  }, []);

  const setSections = useCallback((updater: React.SetStateAction<TrackSection[]>) => {
    markUserEdit();
    setSectionsRaw(updater);
  }, [markUserEdit]);
  const setSectionInputs = useCallback(
    (updater: React.SetStateAction<Record<string, { name: string; alias: string }>>) => {
      markUserEdit();
      setSectionInputsRaw(updater);
    },
    [markUserEdit]
  );
  const setDraftEdits = useCallback(
    (
      updater: React.SetStateAction<
        Record<string, { name: string; aliases: string[]; wildcardChar?: string }>
      >
    ) => {
      markUserEdit();
      setDraftEditsRaw(updater);
    },
    [markUserEdit]
  );
  const setCriteriaHoursByKey = useCallback(
    (updater: React.SetStateAction<Record<string, string>>) => {
      markUserEdit();
      setCriteriaHoursByKeyRaw(updater);
    },
    [markUserEdit]
  );
  const setTrackAliasOrderByGrade = useCallback(
    (updater: React.SetStateAction<Record<string, Record<string, string[]>>>) => {
      markUserEdit();
      setTrackAliasOrderByGradeRaw(updater);
    },
    [markUserEdit]
  );
  const setTrackProgramModeByGrade = useCallback(
    (updater: React.SetStateAction<Record<string, Record<string, TrackProgramMode>>>) => {
      markUserEdit();
      setTrackProgramModeByGradeRaw(updater);
    },
    [markUserEdit]
  );
  const setTrackProgramSyncRepByGrade = useCallback(
    (updater: React.SetStateAction<Record<string, Record<string, string>>>) => {
      markUserEdit();
      setTrackProgramSyncRepByGradeRaw(updater);
    },
    [markUserEdit]
  );
  const setTrackCategoryByGrade = useCallback(
    (updater: React.SetStateAction<Record<string, Record<string, TrackCategory>>>) => {
      markUserEdit();
      setTrackCategoryByGradeRaw(updater);
    },
    [markUserEdit]
  );
  const setLearningPublisherByGrade = useCallback(
    (updater: React.SetStateAction<Record<string, Record<string, string>>>) => {
      markUserEdit();
      setLearningPublisherByGradeRaw(updater);
    },
    [markUserEdit]
  );
  const setHoursAdjustByGrade = useCallback(
    (
      updater: React.SetStateAction<
        Record<string, Record<string, TrackHoursAdjustTrackSettings>>
      >
    ) => {
      markUserEdit();
      setHoursAdjustByGradeRaw(updater);
    },
    [markUserEdit]
  );
  const setPendingAdds = useCallback(
    (
      updater: React.SetStateAction<
        { sectionId: string; gradeIds: string[]; name: string; aliases: string[]; wildcardChar?: string; id: string }[]
      >
    ) => {
      markUserEdit();
      setPendingAddsRaw(updater);
    },
    [markUserEdit]
  );
  const setSectionColumnOrder = useCallback(
    (updater: React.SetStateAction<Record<string, string[]>>) => {
      markUserEdit();
      setSectionColumnOrderRaw(updater);
    },
    [markUserEdit]
  );
  const setPendingTrackGroupDeletes = useCallback(
    (updater: React.SetStateAction<{ sectionId: string; group: Track[] }[]>) => {
      markUserEdit();
      setPendingTrackGroupDeletesRaw(updater);
    },
    [markUserEdit]
  );
  const setPendingOrphanGradeTrackWipes = useCallback(
    (updater: React.SetStateAction<string[]>) => {
      markUserEdit();
      setPendingOrphanGradeTrackWipesRaw(updater);
    },
    [markUserEdit]
  );
  const setSectionSourceGradeIdBySection = useCallback(
    (updater: React.SetStateAction<Record<string, string>>) => {
      markUserEdit();
      setSectionSourceGradeIdBySectionRaw(updater);
    },
    [markUserEdit]
  );

  const { setGradeTrackDirty, saveGradeTrackRef, bumpSidebarTracksEpoch } = useContext(GradeTrackLeaveContext);
  const classSettingsModal = useClassSettingsModal();

  /** 마지막 저장 시점 스냅샷(문자열) - 이와 다르면 dirty */
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState('');
  /** 워크스페이스당 최초 로드 직후 1회만 기준 스냅샷을 잡음 (그 외 load()는 더티를 지우지 않음) */
  const initialBaselineWorkspaceIdRef = useRef<string | null>(null);
  /** 저장/팀장 가져오기 등 서버와 다시 맞춘 뒤 다음 로딩 종료 시 기준 스냅샷 갱신 */
  const refreshBaselineAfterNextLoadEndRef = useRef(false);
  /**
   * 이 페이지에서 저장 직후 `emitAnnualPrerequisiteRefresh`를 보내면 자기 리스너가 또 load()를 돌린다.
   * 그때 loading이 이미 false라 기준 스냅샷이 재로드·후속 effect 전에 잡혀 isDirty가 다시 true가 된다.
   */
  const skipSelfPrerequisiteRefreshOnceRef = useRef(false);
  const prevLoadingRef = useRef(loading);
  const getSnapshotRef = useRef<() => string>(() => '');
  /** 폐기 후 `setLastSavedSnapshot` 재캡처 타이머 — 이탈로 언마운트되면 취소 */
  /** 브라우저 `setTimeout` 핸들은 `number`(Node 타입과 달라 TS 충돌 방지) */
  const discardResyncBaselineTimeoutRef = useRef<number | null>(null);
  /** 기준 스냅샷 재캡처 예약을 구분하는 세대값 — 오래된 예약이 늦게 발화해 최신 기준을 덮어쓰지 않도록 함 */
  const baselineCaptureGenRef = useRef(0);

  const getSnapshot = useCallback(
    () =>
      JSON.stringify({
        sections,
        sectionInputs,
        draftEdits,
        criteriaHoursByKey,
        trackAliasOrderByGrade,
        trackProgramModeByGrade,
        trackProgramSyncRepByGrade,
        trackCategoryByGrade,
        learningPublisherByGrade,
        hoursAdjustByGrade,
        pendingAdds,
        sectionColumnOrder,
        pendingTrackGroupDeletes: pendingTrackGroupDeletes.map((p) => ({
          sectionId: p.sectionId,
          ids: p.group.map((t) => t.id),
        })),
        pendingOrphanGradeTrackWipes,
        sectionSourceGradeIdBySection,
      }),
    [
      sections,
      sectionInputs,
      draftEdits,
      criteriaHoursByKey,
      trackAliasOrderByGrade,
      trackProgramModeByGrade,
      trackProgramSyncRepByGrade,
      trackCategoryByGrade,
      learningPublisherByGrade,
      hoursAdjustByGrade,
      pendingAdds,
      sectionColumnOrder,
      pendingTrackGroupDeletes,
      pendingOrphanGradeTrackWipes,
      sectionSourceGradeIdBySection,
    ]
  );

  getSnapshotRef.current = getSnapshot;
  /**
   * 기준 스냅샷이 있으면 문자열 비교가 1차 진실(되돌리면 clean).
   * 다만 load 직후 기준 시수·컬럼 순서 등 Raw 자동 동기화가 기준 스냅샷보다 늦게 돌면
   * 스냅샷만 달라져 "수정 없음"인데도 dirty가 된다. 사용자 편집 epoch가 기준과 같을 때는
   * 그 드리프트를 dirty로 보지 않는다.
   * 캐시 즉시 로드로 기준이 아직 비어 있을 때만 편집 카운터로 dirty를 본다.
   */
  const isDirty =
    lastSavedSnapshot === ''
      ? userEditEpochRef.current > baselineEditEpochRef.current
      : getSnapshot() !== lastSavedSnapshot &&
        userEditEpochRef.current > baselineEditEpochRef.current;

  /**
   * 자동 동기화로 스냅샷만 바뀐 경우(사용자 미편집) 기준 스냅샷을 현재 화면과 맞춘다.
   * 이후 실제 편집·되돌리기 비교가 어긋나지 않게 한다.
   */
  useEffect(() => {
    if (!workspaceId || loading || lastSavedSnapshot === '') return;
    if (userEditEpochRef.current > baselineEditEpochRef.current) return;
    const current = getSnapshot();
    if (current === lastSavedSnapshot) return;
    setLastSavedSnapshot(current);
  }, [workspaceId, loading, lastSavedSnapshot, getSnapshot]);

  /** 섹션(클래스)별 와일드카드 사용 횟수 — 중복 표시는 같은 섹션 안에서만 */
  const wildcardCountsBySection = React.useMemo(() => {
    const bySection = new Map<string, Map<string, number>>();
    sections.forEach((section) => {
      const countByChar = new Map<string, number>();
      bySection.set(section.id, countByChar);
      const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
      const sectionTracks = sourceGradeId
        ? tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId)
        : [];
      const pendingForSection = pendingAdds.filter((p) => p.sectionId === section.id);
      const displayOrder = buildSectionDisplayColumnIds({
        sectionTracks,
        orderedNames: sourceGradeId ? trackOrderByGrade[sourceGradeId] ?? [] : [],
        pendingForSection: pendingForSection.map((p, globalIndex) => ({ p, globalIndex })),
        deletedTrackIds: new Set(
          pendingTrackGroupDeletes
            .filter((p) => p.sectionId === section.id)
            .map((p) => p.group[0]?.id)
            .filter((id): id is string => Boolean(id))
        ),
        savedOrder: sectionColumnOrder[section.id],
        layoutColumnNames: (section.trackColumns ?? [])
          .map((c) => c.name.trim())
          .filter((n) => n.length > 0),
      });
      displayOrder.forEach((columnId) => {
        const anchor = sectionTracks.find((t) => t.id === columnId);
        if (anchor) {
          const key = `${section.id}-${anchor.id}`;
          const editedName = draftEdits[key]?.name?.trim() || anchor.name.trim();
          const wildcard = sanitizeWildcardChar(
            draftEdits[key]?.wildcardChar ??
              section.trackColumns?.find((col) => col.name.trim() === editedName)?.wildcardChar ??
              ''
          );
          if (wildcard) countByChar.set(wildcard, (countByChar.get(wildcard) ?? 0) + 1);
          return;
        }
        const pending = pendingForSection.find((p) => p.id === columnId);
        if (!pending) return;
        const wildcard = sanitizeWildcardChar(pending.wildcardChar ?? '');
        if (wildcard) countByChar.set(wildcard, (countByChar.get(wildcard) ?? 0) + 1);
      });
    });
    return bySection;
  }, [
    sections,
    sectionSourceGradeIdBySection,
    tracks,
    pendingAdds,
    trackOrderByGrade,
    pendingTrackGroupDeletes,
    sectionColumnOrder,
    draftEdits,
  ]);

  useEffect(() => {
    initialBaselineWorkspaceIdRef.current = null;
    baselineCaptureGenRef.current += 1;
    userEditEpochRef.current = 0;
    baselineEditEpochRef.current = 0;
    setLastSavedSnapshot('');
  }, [workspaceId]);

  const scheduleBaselineCapture = useCallback(
    (opts: { markWorkspaceBaseline: boolean; reason: 'first' | 'resync' | 'fallback' }) => {
      if (!workspaceId) return;
      const gen = ++baselineCaptureGenRef.current;
      const editEpochAtSchedule = userEditEpochRef.current;
      window.setTimeout(() => {
        if (gen !== baselineCaptureGenRef.current) return;
        /**
         * 대기 중 사용자가 편집했으면 그 편집본을 "저장됨" 기준으로 삼지 않는다.
         * 기준이 비어 있으면 비운 채로 두어 epoch dirty가 유지되고,
         * 이미 기준이 있으면 그대로 둔다.
         */
        if (userEditEpochRef.current > editEpochAtSchedule) {
          if (opts.markWorkspaceBaseline) {
            initialBaselineWorkspaceIdRef.current = workspaceId;
          }
          bumpDirtyProbe((n) => n + 1);
          return;
        }
        setLastSavedSnapshot(getSnapshotRef.current());
        baselineEditEpochRef.current = editEpochAtSchedule;
        if (opts.markWorkspaceBaseline) {
          initialBaselineWorkspaceIdRef.current = workspaceId;
        }
        bumpDirtyProbe((n) => n + 1);
      }, 32);
    },
    [workspaceId]
  );

  /**
   * 로딩이 끝날 때마다 기준 스냅샷을 갱신하면 안 됨: 팀장 가져오기 등으로 load()만 돌아도
   * 사용자가 수정한 상태가 곧바로 "저장됨"으로 간주되어 저장 버튼이 비활성화됨.
   * 최초 로드 1회(워크스페이스당) + 저장 직후/팀장 가져오기 직후 등 명시적으로 요청한 경우만 갱신.
   *
   * 주의: 로컬 캐시 hit로 load()가 즉시 끝나면 React 18 배치로 loading true→false가 한 렌더에
   * 흡수되어 justStoppedLoading을 못 볼 수 있다. 아래 fallback effect가 그 경우를 보완한다.
   */
  useEffect(() => {
    const justStoppedLoading = prevLoadingRef.current && !loading;
    prevLoadingRef.current = loading;
    if (!justStoppedLoading || !workspaceId) return;

    const afterResync = refreshBaselineAfterNextLoadEndRef.current;
    const firstBaselineForWorkspace = initialBaselineWorkspaceIdRef.current !== workspaceId;
    if (!afterResync && !firstBaselineForWorkspace) return;

    if (afterResync) refreshBaselineAfterNextLoadEndRef.current = false;

    scheduleBaselineCapture({
      markWorkspaceBaseline: true,
      reason: afterResync ? 'resync' : 'first',
    });
  }, [loading, workspaceId, scheduleBaselineCapture]);

  /** loading true→false 전환을 놓친 경우(캐시 즉시 로드 배치) 기준 스냅샷이 비어 있으면 한 번 보정.
   * 사용자가 이미 편집한 뒤에는 현재 화면을 기준으로 덮어쓰지 않는다. */
  useEffect(() => {
    if (!workspaceId || loading || lastSavedSnapshot !== '') return;
    if (initialBaselineWorkspaceIdRef.current === workspaceId) return;
    if (userEditEpochRef.current > baselineEditEpochRef.current) return;
    scheduleBaselineCapture({ markWorkspaceBaseline: true, reason: 'fallback' });
  }, [workspaceId, loading, lastSavedSnapshot, sections, tracks, scheduleBaselineCapture]);

  useEffect(() => {
    return () => {
      if (discardResyncBaselineTimeoutRef.current != null) {
        window.clearTimeout(discardResyncBaselineTimeoutRef.current);
        discardResyncBaselineTimeoutRef.current = null;
      }
    };
  }, []);

  /** 이탈 확인용: 상위에 dirty 상태 보고 */
  useEffect(() => {
    setGradeTrackDirty(isDirty);
    return () => setGradeTrackDirty(false);
  }, [isDirty, setGradeTrackDirty]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const load = useCallback(async (options?: { skipLocalCache?: boolean }): Promise<{ gradeRows: Grade[]; trackRows: Track[] }> => {
    const fetchOpts = options?.skipLocalCache ? { skipLocalCache: true } : undefined;
    const [gradeRows, trackRows, sectionRows] = await Promise.all([
      getGrades(workspaceId, fetchOpts),
      getTracks(workspaceId, fetchOpts),
      getGradeTrackSections(workspaceId, fetchOpts).catch(() => [{ id: 'base', gradeIds: [] }] as TrackSection[]),
    ]);
    const normalizedSections = normalizeClassStandaloneSections(gradeRows, sectionRows, trackRows);
    setGrades(gradeRows);
    setTracks(trackRows);
    /** load()는 서버 동기화이므로 Raw 세터로 직접 반영(편집 카운터 미증가) */
    setSectionsRaw(normalizedSections);
    const seededPendingAdds: typeof pendingAdds = [];
    const seededCriteriaHours: Record<string, string> = {};
    setSectionSourceGradeIdBySectionRaw(() => {
      const next: Record<string, string> = {};
      normalizedSections.forEach((s) => {
        next[s.id] = s.gradeIds[0] ?? '';
      });
      return next;
    });
    setPendingAddsRaw(seededPendingAdds);
    if (Object.keys(seededCriteriaHours).length > 0) {
      setCriteriaHoursByKeyRaw((prev) => ({ ...prev, ...seededCriteriaHours }));
    }

    const orderByGrade: Record<string, string[]> = {};
    const gradeIds = gradeRows.map((g) => g.id);
    if (gradeRows.length === 0) gradeIds.push('');

    for (const gid of gradeIds) {
      const gradeTracks = trackRows.filter((t) => (t.grade_id ?? '') === (gid || ''));
      const names = Array.from(new Set(gradeTracks.map((t) => t.name)));
      const sectionForGrade = normalizedSections.find((s) => s.gradeIds.includes(gid));
      const layoutNames = (sectionForGrade?.trackColumns ?? [])
        .map((c) => c.name.trim())
        .filter((n) => n.length > 0);
      let orderedNames = layoutNames.length > 0 ? mergeTrackNameOrder(layoutNames, names) : names;
      if (typeof window !== 'undefined' && gid !== undefined) {
        const raw = window.localStorage.getItem(`cp.trackOrder.${workspaceId}.${gid}`);
        if (raw) {
          try {
            const stored = JSON.parse(raw) as string[];
            const preferred = layoutNames.length > 0 ? orderedNames : stored;
            orderedNames = mergeTrackNameOrder(preferred, names);
          } catch {
            orderedNames = layoutNames.length > 0 ? mergeTrackNameOrder(layoutNames, names) : names;
          }
        }
        saveTrackOrderForGrade(workspaceId, gid, orderedNames);
      }
      orderByGrade[gid] = orderedNames;
    }
    setTrackOrderByGrade(orderByGrade);

    const columnOrderBySection: Record<string, string[]> = {};
    normalizedSections.forEach((section) => {
      const sourceGradeId = section.gradeIds[0] ?? '';
      if (!sourceGradeId) return;
      const sectionTracks = trackRows.filter((t) => (t.grade_id ?? '') === sourceGradeId);
      const nameOrder = orderedTrackNamesForSection(section, orderByGrade, workspaceId, sourceGradeId);
      const mergedNames = mergeTrackNameOrder(
        nameOrder,
        Array.from(new Set(sectionTracks.map((t) => t.name.trim()).filter(Boolean)))
      );
      const ids = buildSectionColumnIdsFromNames(mergedNames, sectionTracks, []);
      columnOrderBySection[section.id] = ids;
      if (typeof window !== 'undefined') {
        localStorageSetItemWithQuotaRecovery(
          `cp.sectionColumnOrder.${workspaceId}.${section.id}`,
          JSON.stringify(mergedNames)
        );
      }
    });
    setSectionColumnOrderRaw(columnOrderBySection);
    saveTracksCache(workspaceId, trackRows);
    const aliasOrderByGrade: Record<string, Record<string, string[]>> = {};
    gradeRows.forEach((g) => {
      aliasOrderByGrade[g.id] = loadTrackAliasOrderMap(workspaceId, g.id);
    });
    /**
     * 섹션 layout(방금 저장한 trackColumns.aliases)을 시간표명 순서의 권위 있는 소스로 병합한다.
     * tracks 테이블은 name만 정렬되어 동일 편제명 안 순서가 불안정하므로, 표시는 alias order map에 의존한다.
     */
    normalizedSections.forEach((section) => {
      const gid = section.gradeIds[0] ?? '';
      if (!gid) return;
      const map = { ...(aliasOrderByGrade[gid] ?? {}) };
      let changed = false;
      (section.trackColumns ?? []).forEach((col) => {
        const trackName = col.name.trim();
        const aliases = uniqueNonEmptyAliases(col.aliases ?? [], trackName);
        if (!trackName || aliases.length === 0) return;
        const prev = map[trackName] ?? [];
        if (prev.length === aliases.length && prev.every((a, i) => a === aliases[i])) return;
        map[trackName] = aliases;
        changed = true;
      });
      aliasOrderByGrade[gid] = map;
      if (changed) saveTrackAliasOrderMap(workspaceId, gid, map);
    });
    setTrackAliasOrderByGradeRaw(aliasOrderByGrade);
    let serverProgramPrefs: Awaited<ReturnType<typeof getGradeTrackProgramPreferences>> | null = null;
    try {
      /** 기본: LS `cp.gradeTrackProgramPrefs` 있으면 네트워크 생략. 팀장 가져오기 등은 skipLocalCache로 DB 재조회. */
      serverProgramPrefs = await getGradeTrackProgramPreferences(workspaceId, fetchOpts);
    } catch {
      serverProgramPrefs = null;
    }
    const modeByGrade: Record<string, Record<string, TrackProgramMode>> = {};
    const syncRepByGrade: Record<string, Record<string, string>> = {};
    const categoryByGrade: Record<string, Record<string, TrackCategory>> = {};
    gradeRows.forEach((g) => {
      const localMode = loadTrackProgramModeMap(workspaceId, g.id);
      const localRep = loadTrackProgramSyncRepMap(workspaceId, g.id);
      const localCategory: Record<string, TrackCategory> = {};
      trackRows
        .filter((t) => (t.grade_id ?? '') === g.id)
        .forEach((t) => {
          const name = (t.name ?? '').trim();
          if (!name) return;
          if (t.track_category === 'other') {
            localCategory[name] = 'other';
          } else if (!localCategory[name]) {
            localCategory[name] = 'regular';
          }
        });
      /** 엄격 모드: DB prefs를 읽었으면 그대로 쓴다(없는 편제 = 설정 없음). 조회 실패 시에만 LS 거울로 표시 */
      modeByGrade[g.id] = serverProgramPrefs ? { ...(serverProgramPrefs.modes[g.id] ?? {}) } : localMode;
      syncRepByGrade[g.id] = serverProgramPrefs ? { ...(serverProgramPrefs.syncReps[g.id] ?? {}) } : localRep;
      categoryByGrade[g.id] = localCategory;
    });
    if (serverProgramPrefs) reseedGradeTrackProgramPrefsLocalStores(workspaceId, serverProgramPrefs);
    setTrackProgramModeByGradeRaw(modeByGrade);
    setTrackProgramSyncRepByGradeRaw(syncRepByGrade);
    setTrackCategoryByGradeRaw(categoryByGrade);
    setLearningPublisherByGradeRaw(serverProgramPrefs?.learningPublishers ?? {});
    const hoursAdjust: Record<string, Record<string, TrackHoursAdjustTrackSettings>> = {};
    gradeRows.forEach((g) => {
      const fromServer = serverProgramPrefs ? serverProgramPrefs.hoursAdjust[g.id] ?? {} : null;
      const settings = fromServer ?? loadHoursAdjustSettingsCacheFirst(workspaceId, g.id);
      if (Object.keys(settings).length > 0) hoursAdjust[g.id] = { ...settings };
    });
    setHoursAdjustByGradeRaw(hoursAdjust);
    return { gradeRows, trackRows };
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [workspaceId, load]);

  /**
   * keep-alive로 마운트된 뒤 새 클래스가 생기면 App의 grades/currentGradeId는 갱신되지만
   * 이 페이지 내부 grades·sections는 그대로라, currentGradeId로 필터하면 visibleSections가
   * 빈 배열이 되어 본문이 비어 보인다. nav grades 집합이 달라지면 섹션 셸을 즉시 맞춘다.
   * (dirty 편집 내용은 prev sections를 normalize에 넘겨 유지)
   */
  const navGradesSignature = _gradesFromNav
    .map((g) => g.id)
    .sort()
    .join('|');
  useEffect(() => {
    if (!workspaceId) return;
    const localSignature = grades
      .map((g) => g.id)
      .sort()
      .join('|');
    if (navGradesSignature === localSignature) return;

    setGrades(_gradesFromNav);
    /** nav의 클래스 목록 변화에 맞춰 섹션 셸을 다시 맞추는 자동 동기화이므로 Raw 세터 사용(편집 카운터 미증가) */
    setSectionsRaw((prev) => normalizeClassStandaloneSections(_gradesFromNav, prev, tracks));
    setSectionSourceGradeIdBySectionRaw((prev) => {
      const next: Record<string, string> = { ...prev };
      const validSectionIds = new Set<string>();
      _gradesFromNav.forEach((g) => {
        const sid = sectionIdForGrade(g.id);
        validSectionIds.add(sid);
        if (!next[sid]) next[sid] = g.id;
      });
      Object.keys(next).forEach((sid) => {
        if (!validSectionIds.has(sid)) delete next[sid];
      });
      return next;
    });
  }, [workspaceId, navGradesSignature, _gradesFromNav, grades, tracks]);

  /** 연간시간표 전체 가져오기 등: keep-alive에서도 서버·트랙을 다시 맞춤 */
  useEffect(() => {
    if (!workspaceId) return;
    const onRefresh = (event: Event) => {
      const detail = (event as CustomEvent<AnnualPrerequisiteRefreshDetail>).detail;
      if (!detail || detail.workspaceId !== workspaceId) return;
      if (!detail.scopes.includes('grade_track')) return;
      if (skipSelfPrerequisiteRefreshOnceRef.current) {
        skipSelfPrerequisiteRefreshOnceRef.current = false;
        return;
      }
      setLoading(true);
      refreshBaselineAfterNextLoadEndRef.current = true;
      void load({ skipLocalCache: true })
        .then(() => {
          bumpSidebarTracksEpoch();
        })
        .finally(() => setLoading(false));
    };
    window.addEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onRefresh);
  }, [workspaceId, load, bumpSidebarTracksEpoch]);

  /** 드래그·편집 중 트랙 id가 바뀌면 섹션 컬럼 순서를 편제명 기준으로 다시 매핑 (자동 동기화라 Raw 세터 사용) */
  useEffect(() => {
    if (!workspaceId || sections.length === 0 || tracks.length === 0) return;
    setSectionColumnOrderRaw((prev) => {
      let changed = false;
      const next = { ...prev };
      sections.forEach((section) => {
        const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
        if (!sourceGradeId) return;
        const sectionTracks = tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId);
        const trackIdByName = new Map<string, string>();
        sectionTracks.forEach((t) => {
          const n = (t.name ?? '').trim();
          if (n && !trackIdByName.has(n)) trackIdByName.set(n, t.id);
        });
        const existingIds = new Set(trackIdByName.values());
        const resolveStoredEntry = (entry: string): string | null => {
          if (existingIds.has(entry)) return entry;
          const id = trackIdByName.get(entry.trim());
          return id ?? null;
        };

        let nameOrder: string[] = [];
        if (typeof window !== 'undefined') {
          const raw = window.localStorage.getItem(`cp.sectionColumnOrder.${workspaceId}.${section.id}`);
          if (raw) {
            try {
              const stored = JSON.parse(raw) as string[];
              if (Array.isArray(stored)) {
                const seenNames = new Set<string>();
                for (const entry of stored) {
                  if (typeof entry !== 'string') continue;
                  const trimmed = entry.trim();
                  if (!trimmed) continue;
                  if (trackIdByName.has(trimmed) && !seenNames.has(trimmed)) {
                    seenNames.add(trimmed);
                    nameOrder.push(trimmed);
                    continue;
                  }
                  const id = resolveStoredEntry(trimmed);
                  if (!id) continue;
                  const trackName = sectionTracks.find((t) => t.id === id)?.name.trim() ?? '';
                  if (trackName && !seenNames.has(trackName)) {
                    seenNames.add(trackName);
                    nameOrder.push(trackName);
                  }
                }
              }
            } catch {
              // ignore
            }
          }
        }
        if (nameOrder.length === 0) {
          nameOrder = orderedTrackNamesForSection(
            section,
            trackOrderByGrade,
            workspaceId,
            sourceGradeId
          );
        }
        const allNames = Array.from(new Set(sectionTracks.map((t) => t.name.trim()).filter(Boolean)));
        const mergedNames = mergeTrackNameOrder(nameOrder, allNames);
        const ids = buildSectionColumnIdsFromNames(mergedNames, sectionTracks, []);
        const prevIds = prev[section.id] ?? [];
        if (prevIds.length !== ids.length || prevIds.some((id, i) => id !== ids[i])) {
          changed = true;
          next[section.id] = ids;
        }
      });
      return changed ? next : prev;
    });
  }, [workspaceId, sections, tracks, trackOrderByGrade, sectionSourceGradeIdBySection]);

  // 서버에서 tracks 로드 시 기준 시수 초기값만 채움 (이미 사용자 입력이 있으면 덮어쓰지 않음, 자동 동기화라 Raw 세터 사용)
  useEffect(() => {
    if (tracks.length === 0) return;
    setCriteriaHoursByKeyRaw((prev) => {
      const next = { ...prev };
      sections.forEach((section) => {
        const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
        if (!sourceGradeId) return;
        const sectionTracks = tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId);
        const names = Array.from(new Set(sectionTracks.map((t) => t.name)));
        names.forEach((name) => {
          const group = sectionTracks.filter((t) => t.name === name);
          if (group.length > 0) {
            const key = `${section.id}-${group[0].id}`;
            if (prev[key] === undefined)
              next[key] = String(group[0].criteria_hours ?? 0);
          }
        });
      });
      return next;
    });
  }, [tracks, sections, sectionSourceGradeIdBySection]);

  /** 생성 카드에서 편제 추가 — 기존 등록과 같은 로컬 초안 경로(DB 는 '저장하기' 시) */
  const handleAddTrack = (
    sectionId: string,
    targetGradeIds: string[],
    sectionTracks: Track[],
    payload: TrackCreatePayload
  ): boolean => {
    const name = payload.name.trim();
    if (!name) {
      setToastMessage('편제명을 입력한 뒤 등록해 주세요.');
      return false;
    }
    const cleaned = payload.aliases.map((a) => a.trim()).filter((a) => a.length > 0);
    const newAliases = cleaned.length > 0 ? cleaned : [name];
    const existingGroup = sectionTracks.filter((t) => t.name === name);
    if (existingGroup.length > 0) {
      const key = `${sectionId}-${existingGroup[0].id}`;
      const baseAliases = existingGroup.map((t) => (t.description ?? '').trim()).filter(Boolean);
      setDraftEdits((prev) => {
        const currentAliases =
          prev[key]?.aliases ?? (baseAliases.length ? baseAliases : [name]);
        const wildcardChar = sanitizeWildcardChar(prev[key]?.wildcardChar ?? '');
        return {
          ...prev,
          [key]: { name, aliases: [...currentAliases, ...newAliases], wildcardChar: wildcardChar || undefined },
        };
      });
      showInfo('같은 이름의 편제가 있어 시간표명만 추가했습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
      return true;
    }
    const existingPendingIndex = pendingAdds.findIndex(
      (p) => p.sectionId === sectionId && p.name === name
    );
    if (existingPendingIndex >= 0) {
      setPendingAdds((prev) => {
        const next = [...prev];
        next[existingPendingIndex] = {
          ...next[existingPendingIndex],
          aliases: [...next[existingPendingIndex].aliases, ...newAliases],
          wildcardChar: sanitizeWildcardChar(next[existingPendingIndex].wildcardChar ?? '') || undefined,
        };
        return next;
      });
      showInfo('같은 이름의 편제가 있어 시간표명만 추가했습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
      return true;
    }
    const newPendingId = generateId();
    const newGlobalIndex = pendingAdds.length;
    setPendingAdds((prev) => [
      ...prev,
      {
        sectionId,
        gradeIds: targetGradeIds,
        name,
        aliases: newAliases,
        wildcardChar: sanitizeWildcardChar(payload.wildcardChar) || undefined,
        id: newPendingId,
      },
    ]);
    setSectionColumnOrder((prev) => ({
      ...prev,
      [sectionId]: dedupeStrings([...(prev[sectionId] ?? []), newPendingId]),
    }));
    const hours = payload.criteriaHours.replace(/\D/g, '').slice(0, 3);
    if (hours) {
      setCriteriaHoursByKey((prev) => ({ ...prev, [`${sectionId}-pending-${newGlobalIndex}`]: hours }));
    }
    setTrackCategoryForGrades(targetGradeIds, name, payload.category);
    setTermScopeForGrades(targetGradeIds, name, payload.termScope);
    if (payload.publisher.trim()) setLearningPublisherForGrades(targetGradeIds, name, payload.publisher);
    showInfo('편제를 추가했습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
    return true;
  };

  const handleSaveAll = useCallback(async () => {
    setLoading(true);
    showSaving();
    try {
      const [gradeRows, currentTracks] = await Promise.all([getGrades(workspaceId), getTracks(workspaceId)]);
      setGrades(gradeRows);

      const buildSectionColumns = (
        section: TrackSection
      ): PageSectionTrackColumn[] => {
        const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
        const sourceTracks = sourceGradeId
          ? currentTracks.filter((t) => (t.grade_id ?? '') === sourceGradeId)
          : [];

        const deletedNameSet = new Set(
          pendingTrackGroupDeletes
            .filter((p) => p.sectionId === section.id)
            .map((p) => p.group[0]?.name)
            .filter((v): v is string => Boolean(v))
        );

        const sourceNames = Array.from(new Set(sourceTracks.map((t) => t.name))).filter(
          (name) => !deletedNameSet.has(name)
        );
        const sourceNameSet = new Set(sourceNames);
        const savedOrder = sectionColumnOrder[section.id] ?? [];
        const orderedSourceNames = (() => {
          const names: string[] = [];
          const seen = new Set<string>();
          const tryAdd = (trackName: string) => {
            const n = trackName.trim();
            if (!n || !sourceNameSet.has(n) || seen.has(n)) return;
            seen.add(n);
            names.push(n);
          };
          if (savedOrder.length > 0) {
            for (const entry of savedOrder) {
              if (sourceNameSet.has(entry)) tryAdd(entry);
              else {
                const g = sourceTracks.filter((t) => t.id === entry);
                if (g[0]) tryAdd(g[0].name);
              }
            }
          }
          const layoutNameOrder = orderedTrackNamesForSection(
            section,
            trackOrderByGrade,
            workspaceId,
            sourceGradeId
          ).filter((n) => sourceNameSet.has(n));
          for (const n of layoutNameOrder) tryAdd(n);
          for (const n of sourceNames) tryAdd(n);
          return names;
        })();

        const columns: PageSectionTrackColumn[] = [];

        orderedSourceNames.forEach((name) => {
          const group = sourceTracks.filter((t) => t.name === name);
          if (group.length === 0) return;
          const key = `${section.id}-${group[0].id}`;
          const draft = draftEdits[key];
          const aliasesFromServer = group.map((t) => (t.description ?? '').trim()).filter(Boolean);
          const storedAliasOrder = trackAliasOrderByGrade[sourceGradeId]?.[name] ?? [];
          const orderedAliasesFromServer =
            storedAliasOrder.length > 0
              ? [
                  ...storedAliasOrder.filter((alias) => aliasesFromServer.includes(alias)),
                  ...aliasesFromServer.filter((alias) => !storedAliasOrder.includes(alias)),
                ]
              : aliasesFromServer;
          const aliases = uniqueNonEmptyAliases(
            draft?.aliases?.map((a) => a.trim()).filter((a) => a.length > 0) ??
              (orderedAliasesFromServer.length > 0 ? orderedAliasesFromServer : [name]),
            draft?.name?.trim() || name
          );
          const wildcardChar =
            sanitizeWildcardChar(
              draft?.wildcardChar ??
                section.trackColumns?.find((col) => col.name.trim() === name.trim())?.wildcardChar ??
                ''
            ) || undefined;
          const criteriaStr = criteriaHoursByKey[key];
          const criteriaNum = Math.min(
            999,
            Math.max(0, parseInt(criteriaStr ?? String(group[0].criteria_hours ?? 0), 10) || 0)
          );
          const category =
            (trackCategoryByGrade[sourceGradeId]?.[name] ??
              (group.some((row) => row.track_category === 'other') ? 'other' : 'regular')) === 'other'
              ? 'other'
              : 'regular';
          columns.push({
            id: group[0].id,
            name: draft?.name?.trim() || name,
            aliases,
            criteriaHours: criteriaNum,
            trackCategory: category,
            wildcardChar,
          });
        });

        const pendingForSection = pendingAdds
          .map((p, globalIndex) => (p.sectionId === section.id ? { p, globalIndex } : null))
          .filter((v): v is { p: (typeof pendingAdds)[0]; globalIndex: number } => v != null);

        const columnNamesFromServer = new Set(columns.map((c) => c.name.trim()));

        pendingForSection.forEach(({ p, globalIndex }) => {
          const pendingName = p.name.trim();
          if (!pendingName || columnNamesFromServer.has(pendingName)) return;
          const key = `${section.id}-pending-${globalIndex}`;
          const aliases = uniqueNonEmptyAliases(
            p.aliases.map((a) => a.trim()).filter((a) => a.length > 0),
            p.name.trim()
          );
          const criteriaStr = criteriaHoursByKey[key];
          const criteriaNum = Math.min(999, Math.max(0, parseInt(criteriaStr || '0', 10) || 0));
          const category = (trackCategoryByGrade[p.gradeIds[0] ?? '']?.[p.name.trim()] ?? 'regular') === 'other'
            ? 'other'
            : 'regular';
          columns.push({
            id: p.id,
            name: p.name.trim(),
            aliases,
            criteriaHours: criteriaNum,
            trackCategory: category,
            wildcardChar: sanitizeWildcardChar(p.wildcardChar ?? '') || undefined,
          });
        });

        const namesInColumns = new Set(columns.map((c) => c.name.trim()));
        for (const col of section.trackColumns ?? []) {
          const layoutName = col.name.trim();
          if (!layoutName || deletedNameSet.has(layoutName) || namesInColumns.has(layoutName)) continue;
          namesInColumns.add(layoutName);
          columns.push({
            id: col.id,
            name: layoutName,
            aliases: uniqueNonEmptyAliases(
              col.aliases.length > 0 ? col.aliases : [layoutName],
              layoutName
            ),
            criteriaHours: col.criteriaHours ?? 0,
            trackCategory: col.trackCategory === 'other' ? 'other' : 'regular',
            wildcardChar: sanitizeWildcardChar(col.wildcardChar ?? '') || undefined,
          });
        }

        return mergeSectionTrackColumns(columns.filter((c) => c.name.length > 0));
      };

      const sectionsToSave: TrackSection[] = sections.map((section) => {
        const columns = buildSectionColumns(section);
        return {
          ...section,
          sourceGradeId: section.gradeIds[0] || undefined,
          trackColumns: columns,
        };
      });
      const wildcardUsage = new Map<string, number>();
      sectionsToSave.forEach((section) => {
        (section.trackColumns ?? []).forEach((column) => {
          const wildcard = sanitizeWildcardChar(column.wildcardChar ?? '');
          if (!wildcard) return;
          wildcardUsage.set(wildcard, (wildcardUsage.get(wildcard) ?? 0) + 1);
        });
      });
      const hasWildcardDuplicate = Array.from(wildcardUsage.values()).some((count) => count > 1);
      if (hasWildcardDuplicate) {
        showError('편제별 와일드카드 문자가 중복되었습니다. 중복되지 않게 설정 후 저장해주세요.');
        return;
      }

      const renamedTrackNamesByGrade: Record<string, Array<{ oldName: string; newName: string }>> = {};
      sections.forEach((section) => {
        const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
        if (!sourceGradeId) return;
        const deletedNames = new Set(
          pendingTrackGroupDeletes
            .filter((p) => p.sectionId === section.id)
            .map((p) => p.group[0]?.name?.trim())
            .filter((name): name is string => !!name)
        );
        const sourceTracks = currentTracks.filter((t) => (t.grade_id ?? '') === sourceGradeId);
        const sourceNames = Array.from(new Set(sourceTracks.map((t) => t.name.trim()).filter(Boolean)));
        sourceNames.forEach((oldName) => {
          if (deletedNames.has(oldName)) return;
          const group = sourceTracks.filter((t) => t.name === oldName);
          if (group.length === 0) return;
          const key = `${section.id}-${group[0].id}`;
          const newName = draftEdits[key]?.name?.trim();
          if (!newName || newName === oldName) return;
          section.gradeIds.forEach((gradeId) => {
            if (!renamedTrackNamesByGrade[gradeId]) renamedTrackNamesByGrade[gradeId] = [];
            renamedTrackNamesByGrade[gradeId].push({ oldName, newName });
          });
        });
      });

      const deletedTrackNamesByGrade: Record<string, Set<string>> = {};
      pendingTrackGroupDeletes.forEach(({ sectionId, group }) => {
        const deletedName = group[0]?.name?.trim();
        if (!deletedName) return;
        const section = sections.find((s) => s.id === sectionId);
        section?.gradeIds.forEach((gradeId) => {
          if (!deletedTrackNamesByGrade[gradeId]) deletedTrackNamesByGrade[gradeId] = new Set<string>();
          deletedTrackNamesByGrade[gradeId].add(deletedName);
        });
      });

      const savedGradeIds = new Set<string>();
      for (const section of sections) {
        const columns = buildSectionColumns(section);
        for (const gradeId of section.gradeIds) {
          if (savedGradeIds.has(gradeId)) continue;
          savedGradeIds.add(gradeId);

          const deletedTrackNames = Array.from(deletedTrackNamesByGrade[gradeId] ?? []);
          if (deletedTrackNames.length > 0) {
            await deleteTrackProgramDataForGradeTrackNames(workspaceId, gradeId, deletedTrackNames);
          }
          for (const rename of renamedTrackNamesByGrade[gradeId] ?? []) {
            await renameTrackProgramDataForGradeTrackName(
              workspaceId,
              gradeId,
              rename.oldName,
              rename.newName
            );
          }

          await deleteTracksForGrade(workspaceId, gradeId);

          const insertedKeys = new Set<string>();
          for (const column of columns) {
            const trackName = column.name.trim();
            if (!trackName) continue;
            const aliases = uniqueNonEmptyAliases(column.aliases, trackName);
            for (const alias of aliases) {
              const insertKey = `${trackName}\0${alias}`;
              if (insertedKeys.has(insertKey)) continue;
              insertedKeys.add(insertKey);
              await createTrack(workspaceId, {
                gradeId,
                name: trackName,
                description: alias,
                criteriaHours: column.criteriaHours,
                trackCategory: column.trackCategory,
              });
            }
          }
        }
      }

      for (const gradeId of pendingOrphanGradeTrackWipes) {
        if (savedGradeIds.has(gradeId)) continue;
        const wipeNames = Array.from(
          new Set(
            currentTracks
              .filter((t) => (t.grade_id ?? '') === gradeId)
              .map((t) => t.name.trim())
              .filter((name) => name.length > 0)
          )
        );
        if (wipeNames.length > 0) {
          await deleteTrackProgramDataForGradeTrackNames(workspaceId, gradeId, wipeNames);
        }
        await deleteTracksForGrade(workspaceId, gradeId);
      }

      await saveGradeTrackSections(workspaceId, sectionsToSave);
      /** 저장 성공 후 로컬 초안을 새 기준으로 정리하는 것이므로 Raw 세터 사용(편집 카운터 미증가) */
      setPendingAddsRaw([]);
      setPendingTrackGroupDeletesRaw([]);
      setPendingOrphanGradeTrackWipesRaw([]);
      setDraftEditsRaw({});
      setCriteriaHoursByKeyRaw({});
      const newSectionColumnOrder: Record<string, string[]> = {};
      sections.forEach((section) => {
        const columns = buildSectionColumns(section);
        const orderByName = columns.map((c) => c.name.trim()).filter((n) => n.length > 0);
        const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
        const sectionTracks = sourceGradeId
          ? currentTracks.filter((t) => (t.grade_id ?? '') === sourceGradeId)
          : [];
        const pendingForSection = pendingAdds
          .map((p, globalIndex) => (p.sectionId === section.id ? { p, globalIndex } : null))
          .filter((v): v is { p: (typeof pendingAdds)[0]; globalIndex: number } => v != null)
          .map(({ p }) => ({ p }));
        newSectionColumnOrder[section.id] = buildSectionColumnIdsFromNames(
          orderByName,
          sectionTracks,
          pendingForSection
        );
        if (typeof window !== 'undefined') {
          localStorageSetItemWithQuotaRecovery(
            `cp.sectionColumnOrder.${workspaceId}.${section.id}`,
            JSON.stringify(orderByName)
          );
        }
      });
      setSectionColumnOrderRaw(newSectionColumnOrder);
      /** 저장 후 재로드(load) 시 cp.trackOrder가 이전값이면 섹션 순서를 다시 덮어써 되돌아갈 수 있어 함께 동기화 */
      const nextTrackOrderByGrade: Record<string, string[]> = { ...trackOrderByGrade };
      sections.forEach((section) => {
        const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
        if (!sourceGradeId) return;
        const columns = buildSectionColumns(section);
        const order = columns.map((c) => c.name.trim()).filter((n) => n.length > 0);
        nextTrackOrderByGrade[sourceGradeId] = order;
        saveTrackOrderForGrade(workspaceId, sourceGradeId, order);
      });
      setTrackOrderByGrade(nextTrackOrderByGrade);
      const validTrackNamesByGrade: Record<string, Set<string>> = {};
      const aliasOrderByGradeToPersist: Record<string, Record<string, string[]>> = {};
      sections.forEach((section) => {
        const columns = buildSectionColumns(section);
        section.gradeIds.forEach((gid) => {
          if (!validTrackNamesByGrade[gid]) validTrackNamesByGrade[gid] = new Set<string>();
          if (!aliasOrderByGradeToPersist[gid]) aliasOrderByGradeToPersist[gid] = {};
          columns.forEach((c) => {
            const trackName = c.name.trim();
            if (!trackName) return;
            validTrackNamesByGrade[gid].add(trackName);
            aliasOrderByGradeToPersist[gid][trackName] = c.aliases
              .map((a) => a.trim())
              .filter((a) => a.length > 0);
          });
        });
      });
      const serverModes: Record<string, Record<string, TrackProgramMode>> = {};
      const serverSyncReps: Record<string, Record<string, string>> = {};
      const nextTrackCategoryByGrade: Record<string, Record<string, TrackCategory>> = {};
      gradeRows.forEach((g) => {
        const currentModeMap = trackProgramModeByGrade[g.id] ?? {};
        const currentRepMap = trackProgramSyncRepByGrade[g.id] ?? {};
        const currentCategoryMap = trackCategoryByGrade[g.id] ?? {};
        const validNames = validTrackNamesByGrade[g.id] ?? new Set<string>();
        const aliasForGrade = aliasOrderByGradeToPersist[g.id] ?? {};
        const persistedMode: Record<string, TrackProgramMode> = {};
        const persistedRep: Record<string, string> = {};
        const persistedCategory: Record<string, TrackCategory> = {};
        Object.entries(currentModeMap).forEach(([trackName, mode]) => {
          if (!trackName.trim()) return;
          if (!validNames.has(trackName)) return;
          if (mode === 'unified' || mode === 'split' || mode === 'sync') {
            persistedMode[trackName] = mode;
          }
        });
        Object.entries(persistedMode).forEach(([trackName, mode]) => {
          if (mode !== 'sync') return;
          const aliases = (aliasForGrade[trackName] ?? []).map((a) => a.trim()).filter((a) => a.length > 0);
          if (aliases.length < 2) return;
          let rep = (currentRepMap[trackName] ?? '').trim();
          if (!rep || !aliases.includes(rep)) rep = aliases[0];
          persistedRep[trackName] = rep;
        });
        Object.entries(currentCategoryMap).forEach(([trackName, category]) => {
          const tn = trackName.trim();
          if (!tn) return;
          if (category === 'other') persistedCategory[tn] = 'other';
        });
        serverModes[g.id] = persistedMode;
        serverSyncReps[g.id] = persistedRep;
        nextTrackCategoryByGrade[g.id] = persistedCategory;
        /** mode/syncRep LS 거울은 아래 prefs DB 저장 성공 후 saveGradeTrackProgramPreferences가 엄격 재시드 */
        saveTrackAliasOrderMap(workspaceId, g.id, aliasForGrade);
      });
      const cachedHoursAdjust =
        loadGradeTrackProgramPreferencesCache(workspaceId)?.hoursAdjust ?? {};
      const serverHoursAdjust: GradeTrackProgramPreferences['hoursAdjust'] = {};
      gradeRows.forEach((g) => {
        const validNames = validTrackNamesByGrade[g.id] ?? new Set<string>();
        const current = hoursAdjustByGrade[g.id] ?? cachedHoursAdjust[g.id] ?? {};
        const nextGrade: GradeTrackProgramPreferences['hoursAdjust'][string] = {};
        Object.entries(current).forEach(([trackName, settings]) => {
          const tn = trackName.trim();
          if (!tn || !validNames.has(tn)) return;
          nextGrade[tn] = settings;
        });
        if (Object.keys(nextGrade).length > 0) serverHoursAdjust[g.id] = nextGrade;
      });
      const serverLearningPublishers: GradeTrackProgramPreferences['learningPublishers'] = {};
      gradeRows.forEach((g) => {
        const validNames = validTrackNamesByGrade[g.id] ?? new Set<string>();
        const current = learningPublisherByGrade[g.id] ?? {};
        const nextGrade: Record<string, string> = {};
        Object.entries(current).forEach(([trackName, publisher]) => {
          const tn = trackName.trim();
          const pub = (publisher ?? '').trim();
          if (!tn || !pub || !validNames.has(tn)) return;
          nextGrade[tn] = pub;
        });
        if (Object.keys(nextGrade).length > 0) serverLearningPublishers[g.id] = nextGrade;
      });
      await saveGradeTrackProgramPreferences(workspaceId, {
        modes: serverModes,
        syncReps: serverSyncReps,
        hoursAdjust: serverHoursAdjust,
        learningPublishers: serverLearningPublishers,
      });
      /**
       * preferences 저장의 invalidate가 순서 LS를 비우던 회귀를 막기 위해,
       * 최종 저장 직후 편제·시간표명 순서를 한 번 더 확정하고 DB를 강제 재조회한다.
       */
      sections.forEach((section) => {
        const columns = buildSectionColumns(section);
        const orderByName = columns.map((c) => c.name.trim()).filter((n) => n.length > 0);
        if (typeof window !== 'undefined') {
          localStorageSetItemWithQuotaRecovery(
            `cp.sectionColumnOrder.${workspaceId}.${section.id}`,
            JSON.stringify(orderByName)
          );
        }
        const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
        if (sourceGradeId) {
          saveTrackOrderForGrade(workspaceId, sourceGradeId, orderByName);
        }
      });
      gradeRows.forEach((g) => {
        const aliasForGrade = aliasOrderByGradeToPersist[g.id] ?? {};
        saveTrackAliasOrderMap(workspaceId, g.id, aliasForGrade);
      });
      setTrackAliasOrderByGradeRaw(aliasOrderByGradeToPersist);
      setTrackCategoryByGradeRaw(nextTrackCategoryByGrade);
      refreshBaselineAfterNextLoadEndRef.current = true;
      await load({ skipLocalCache: true });
      bumpSidebarTracksEpoch();
      skipSelfPrerequisiteRefreshOnceRef.current = true;
      emitAnnualPrerequisiteRefresh(workspaceId, ['grade_track']);
      showSuccess();
    } catch (error: any) {
      showError(error?.message ?? '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [
    criteriaHoursByKey,
    draftEdits,
    load,
    pendingAdds,
    pendingTrackGroupDeletes,
    pendingOrphanGradeTrackWipes,
    workspaceId,
    sectionColumnOrder,
    sectionSourceGradeIdBySection,
    sections,
    trackOrderByGrade,
    trackAliasOrderByGrade,
    trackProgramModeByGrade,
    trackProgramSyncRepByGrade,
    trackCategoryByGrade,
    learningPublisherByGrade,
    hoursAdjustByGrade,
    bumpSidebarTracksEpoch,
    showError,
    showSaving,
    showSuccess,
  ]);

  const handleDiscardUnsaved = useCallback(() => {
    const raw = lastSavedSnapshot;
    if (!raw) return;
    type SnapPending = { sectionId: string; ids: string[] };
    type SnapType = {
      sections?: PageSectionLayout[];
      sectionInputs?: Record<string, { name: string; alias: string }>;
      draftEdits?: Record<string, { name: string; aliases: string[]; wildcardChar?: string }>;
      criteriaHoursByKey?: Record<string, string>;
      trackAliasOrderByGrade?: Record<string, Record<string, string[]>>;
      trackProgramModeByGrade?: Record<string, Record<string, TrackProgramMode>>;
      trackProgramSyncRepByGrade?: Record<string, Record<string, string>>;
      trackCategoryByGrade?: Record<string, Record<string, TrackCategory>>;
      learningPublisherByGrade?: Record<string, Record<string, string>>;
      hoursAdjustByGrade?: Record<string, Record<string, TrackHoursAdjustTrackSettings>>;
      pendingAdds?: {
        sectionId: string;
        gradeIds: string[];
        name: string;
        aliases: string[];
        wildcardChar?: string;
        id: string;
      }[];
      sectionColumnOrder?: Record<string, string[]>;
      pendingTrackGroupDeletes?: SnapPending[];
      pendingOrphanGradeTrackWipes?: string[];
      sectionSourceGradeIdBySection?: Record<string, string>;
    };
    let snap: SnapType;
    try {
      snap = JSON.parse(raw) as SnapType;
    } catch {
      return;
    }
    const trackById = new Map(tracks.map((t) => [t.id, t]));
    const pendingRaw = snap.pendingTrackGroupDeletes ?? [];
    const restoredPending = pendingRaw
      .map((p) => ({
        sectionId: p.sectionId,
        group: (p.ids ?? []).map((id) => trackById.get(id)).filter((t): t is Track => t != null),
      }))
      .filter((p) => p.group.length > 0);

    /** 폐기는 사용자 편집이 아니라 이전 기준으로 되돌리는 동작이므로 Raw 세터로 직접 반영(편집 카운터 미증가) */
    setSectionsRaw(snap.sections ?? []);
    setSectionInputsRaw(snap.sectionInputs ?? {});
    setDraftEditsRaw(snap.draftEdits ?? {});
    setCriteriaHoursByKeyRaw(snap.criteriaHoursByKey ?? {});
    setTrackAliasOrderByGradeRaw(snap.trackAliasOrderByGrade ?? {});
    setTrackProgramModeByGradeRaw(snap.trackProgramModeByGrade ?? {});
    setTrackProgramSyncRepByGradeRaw(snap.trackProgramSyncRepByGrade ?? {});
    setTrackCategoryByGradeRaw(snap.trackCategoryByGrade ?? {});
    setLearningPublisherByGradeRaw(snap.learningPublisherByGrade ?? {});
    setHoursAdjustByGradeRaw(snap.hoursAdjustByGrade ?? {});
    setPendingAddsRaw(snap.pendingAdds ?? []);
    setSectionColumnOrderRaw(snap.sectionColumnOrder ?? {});
    setSectionSourceGradeIdBySectionRaw(snap.sectionSourceGradeIdBySection ?? {});
    setPendingTrackGroupDeletesRaw(restoredPending);
    setPendingOrphanGradeTrackWipesRaw(snap.pendingOrphanGradeTrackWipes ?? []);
    setPendingDeleteGroup(null);
    setCopiedSectionData(null);
    setPasteTargetSectionId(null);
    setLeaderImportTargetSectionId(null);
    queueMicrotask(() => window.dispatchEvent(new CustomEvent(TRACK_PROGRAM_SPLIT_CHANGE_EVENT)));
    /**
     * 폐기 직후 `sectionColumnOrder`·기준시수 등 다른 effect가 LS/트랙 순서로 상태를 한 번 더 맞춘다.
     * 그때까지 `lastSavedSnapshot`을 옛 문자열로 두면 곧바로 isDirty가 다시 true가 된다(저장하기 활성·이탈 후에도 동일).
     * 초기 로드와 같이 다음 틱에 현재 화면을 새 기준선으로 잡는다. editEpochAtSchedule을 고정해
     * 그 사이 사용자가 다시 편집하면 isDirty가 곧바로 true로 유지되게 한다.
     */
    if (discardResyncBaselineTimeoutRef.current != null) {
      window.clearTimeout(discardResyncBaselineTimeoutRef.current);
    }
    const discardGen = ++baselineCaptureGenRef.current;
    const editEpochAtDiscard = userEditEpochRef.current;
    discardResyncBaselineTimeoutRef.current = window.setTimeout(() => {
      discardResyncBaselineTimeoutRef.current = null;
      if (discardGen !== baselineCaptureGenRef.current) return;
      setLastSavedSnapshot(getSnapshotRef.current());
      baselineEditEpochRef.current = editEpochAtDiscard;
      bumpDirtyProbe((n) => n + 1);
    }, 0);
  }, [lastSavedSnapshot, tracks]);

  /** 이탈 확인 모달에서 "저장 하기" 시 호출할 수 있도록 등록 */
  useEffect(() => {
    saveGradeTrackRef.current = { save: handleSaveAll, discard: handleDiscardUnsaved };
    return () => {
      saveGradeTrackRef.current = null;
    };
  }, [saveGradeTrackRef, handleSaveAll, handleDiscardUnsaved]);

  const getOrderedTrackNames = (gradeIds: string[]): string[] => {
    if (gradeIds.length === 0) return [];
    const primary = gradeIds[0];
    const primaryOrder = trackOrderByGrade[primary] ?? [];
    const sectionTracks = tracks.filter((t) =>
      gradeIds.includes(t.grade_id ?? '')
    );
    const allNames = Array.from(new Set(sectionTracks.map((t) => t.name)));
    const existing = primaryOrder.filter((n) => allNames.includes(n));
    const additions = allNames.filter((n) => !primaryOrder.includes(n));
    return [...existing, ...additions];
  };

  const setTrackProgramModeForGrades = useCallback(
    (gradeIds: string[], trackName: string, mode: TrackProgramMode, aliasesHint?: string[]) => {
      if (!trackName.trim() || gradeIds.length === 0) return;
      setTrackProgramModeByGrade((prev) => {
        const next = { ...prev };
        gradeIds.forEach((gradeId) => {
          const cur = next[gradeId] ?? {};
          next[gradeId] = { ...cur, [trackName]: mode };
        });
        return next;
      });
      if (mode !== 'sync') {
        queueMicrotask(() => window.dispatchEvent(new CustomEvent(TRACK_PROGRAM_SPLIT_CHANGE_EVENT)));
        return;
      }
      setTrackProgramSyncRepByGrade((prev) => {
        const next = { ...prev };
        gradeIds.forEach((gradeId) => {
          const hint = (aliasesHint ?? []).map((a) => a.trim()).filter((a) => a.length > 0);
          const storedOrder = trackAliasOrderByGrade[gradeId]?.[trackName] ?? [];
          const nonEmpty = hint.length
            ? hint
            : storedOrder.map((a) => a.trim()).filter((a) => a.length > 0);
          if (nonEmpty.length < 2) return;
          const prevRep = (prev[gradeId]?.[trackName] ?? '').trim();
          const rep = prevRep && nonEmpty.includes(prevRep) ? prevRep : nonEmpty[0];
          next[gradeId] = { ...(next[gradeId] ?? {}), [trackName]: rep };
        });
        return next;
      });
      queueMicrotask(() => window.dispatchEvent(new CustomEvent(TRACK_PROGRAM_SPLIT_CHANGE_EVENT)));
    },
    [trackAliasOrderByGrade]
  );

  const setTrackProgramSyncRepForGrades = useCallback(
    (gradeIds: string[], trackName: string, alias: string) => {
      const t = alias.trim();
      if (!trackName.trim() || !t || gradeIds.length === 0) return;
      setTrackProgramSyncRepByGrade((prev) => {
        const next = { ...prev };
        gradeIds.forEach((gradeId) => {
          next[gradeId] = { ...(next[gradeId] ?? {}), [trackName]: t };
        });
        return next;
      });
      queueMicrotask(() => window.dispatchEvent(new CustomEvent(TRACK_PROGRAM_SPLIT_CHANGE_EVENT)));
    },
    []
  );

  const setTrackCategoryForGrades = useCallback(
    (gradeIds: string[], trackName: string, category: TrackCategory) => {
      const tn = trackName.trim();
      if (!tn || gradeIds.length === 0) return;
      setTrackCategoryByGrade((prev) => {
        const next = { ...prev };
        gradeIds.forEach((gradeId) => {
          next[gradeId] = { ...(next[gradeId] ?? {}), [tn]: category };
        });
        return next;
      });
      queueMicrotask(() => window.dispatchEvent(new CustomEvent(TRACK_PROGRAM_SPLIT_CHANGE_EVENT)));
    },
    []
  );

  const setLearningPublisherForGrades = useCallback(
    (gradeIds: string[], trackName: string, publisher: string) => {
      const tn = trackName.trim();
      if (!tn || gradeIds.length === 0) return;
      const pub = publisher.trim();
      setLearningPublisherByGrade((prev) => {
        const next = { ...prev };
        gradeIds.forEach((gradeId) => {
          const map = { ...(next[gradeId] ?? {}) };
          if (pub) map[tn] = pub;
          else delete map[tn];
          next[gradeId] = map;
        });
        return next;
      });
    },
    []
  );

  const setTermScopeForGrades = useCallback(
    (gradeIds: string[], trackName: string, termScope: TrackHoursTermScope) => {
      const tn = trackName.trim();
      if (!tn || gradeIds.length === 0) return;
      const needsChange = gradeIds.some(
        (gradeId) => (hoursAdjustByGrade[gradeId]?.[tn]?.termScope ?? 'split') !== termScope
      );
      if (!needsChange) return;
      setHoursAdjustByGrade((prev) => {
        const next = { ...prev };
        gradeIds.forEach((gradeId) => {
          const map = { ...(next[gradeId] ?? {}) };
          const cur = getTrackHoursAdjustSettings(map, tn);
          map[tn] = { ...cur, termScope };
          next[gradeId] = map;
        });
        /** 편집 중 값은 저장 전까지 화면 상태에만 둔다 — prefs LS·거울은 DB 저장 성공 시에만 갱신(엄격 모드) */
        return next;
      });
    },
    [hoursAdjustByGrade, setHoursAdjustByGrade]
  );

  const applyUnifiedScopeIfTemplateUnified = useCallback(
    (gradeIds: string[], trackName: string, templateName: string) => {
      if (!isUnifiedHoursAdjustTrack(templateName, hoursAdjustByGrade)) return;
      setTermScopeForGrades(gradeIds, trackName, 'unified');
    },
    [hoursAdjustByGrade, setTermScopeForGrades]
  );

  const setTrackAliasOrderForGrades = useCallback(
    (gradeIds: string[], trackName: string, aliases: string[]) => {
      const normalized = Array.from(new Set(aliases.map((a) => a.trim()).filter((a) => a.length > 0)));
      if (gradeIds.length === 0 || !trackName.trim() || normalized.length === 0) return;
      setTrackAliasOrderByGrade((prev) => {
        const next = { ...prev };
        gradeIds.forEach((gradeId) => {
          const current = next[gradeId] ?? {};
          next[gradeId] = { ...current, [trackName]: normalized };
        });
        return next;
      });
    },
    []
  );

  const collectBatchLearningTargets = useCallback((): ProgramLearningBatchGenerateTarget[] => {
    const targetSections =
      currentGradeId === ''
        ? sections
        : sections.filter((section) => section.gradeIds.includes(currentGradeId));
    const targets: ProgramLearningBatchGenerateTarget[] = [];
    targetSections.forEach((section) => {
      const gradeIds = section.gradeIds;
      const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
      const gradeId = sourceGradeId || gradeIds[0] || '';
      if (!gradeId) return;
      const grade = grades.find((g) => g.id === gradeId);
      const gradeLabel = grade ? parseClassName(grade.name).title : '클래스';
      const sectionTracks = sourceGradeId
        ? tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId)
        : [];
      const deletedTrackIdsForSection = new Set(
        pendingTrackGroupDeletes
          .filter((p) => p.sectionId === section.id)
          .map((p) => p.group[0]?.id)
          .filter((id): id is string => Boolean(id))
      );
      const pendingForSection = pendingAdds
        .map((p, globalIndex) => (p.sectionId === section.id ? { p, globalIndex } : null))
        .filter((x): x is { p: (typeof pendingAdds)[0]; globalIndex: number } => x != null);
      const orderedNames = sourceGradeId ? getOrderedTrackNames([sourceGradeId]) : [];
      const displayOrder = buildSectionDisplayColumnIds({
        sectionTracks,
        orderedNames,
        pendingForSection,
        deletedTrackIds: deletedTrackIdsForSection,
        savedOrder: sectionColumnOrder[section.id],
        layoutColumnNames: (section.trackColumns ?? [])
          .map((c) => c.name.trim())
          .filter((n) => n.length > 0),
      });
      const names: string[] = [];
      const seenNames = new Set<string>();
      displayOrder.forEach((columnId) => {
        const anchor = sectionTracks.find((t) => t.id === columnId);
        const trackName = (anchor?.name ?? '').trim();
        if (!trackName || seenNames.has(trackName)) return;
        seenNames.add(trackName);
        names.push(trackName);
      });
      names.forEach((trackName) => {
        const source = parseTrackLearningSourceSelection(
          consolidatedLearningPublisher(gradeIds, trackName, learningPublisherByGrade),
          trackName
        );
        if (!source.publisher) return;
        const group = sectionTracks.filter((t) => t.name === trackName);
        const rawAliases = group.map((t) => (t.description ?? '').trim()).filter(Boolean);
        const aliases = buildOrderedAliasesForTrack(
          gradeId,
          trackName,
          rawAliases.length > 0 ? rawAliases : [trackName],
          trackAliasOrderByGrade
        );
        const target: ProgramLearningBatchGenerateTarget = {
          gradeId,
          gradeLabel,
          trackName,
          templateName: source.templateName || trackName,
          publisher: source.publisher,
          aliases,
          programMode: consolidatedProgramMode(gradeIds, trackName, trackProgramModeByGrade),
          syncRepAlias: effectiveSyncRepForColumn(
            gradeIds,
            trackName,
            aliases,
            trackAliasOrderByGrade,
            trackProgramSyncRepByGrade
          ),
        };
        if (source.gradeNumber) {
          Object.assign(target, { contentGradeNumber: source.gradeNumber });
        }
        targets.push(target);
      });
    });
    return targets;
  }, [
    currentGradeId,
    grades,
    learningPublisherByGrade,
    pendingAdds,
    pendingTrackGroupDeletes,
    sectionColumnOrder,
    sectionSourceGradeIdBySection,
    sections,
    trackAliasOrderByGrade,
    trackOrderByGrade,
    trackProgramModeByGrade,
    trackProgramSyncRepByGrade,
    tracks,
  ]);

  const openBatchGenerateConfirm = useCallback(() => {
    const targets = collectBatchLearningTargets();
    if (!targets.length) {
      showError('출판사를 선택한 편제가 없습니다. 편제 카드에서 출판사를 먼저 선택해 주세요.');
      return;
    }
    setBatchLearningConfirmTargets(targets);
  }, [collectBatchLearningTargets, showError]);

  const handleBatchGenerateLearning = useCallback(async () => {
    const targets = batchLearningConfirmTargets ?? collectBatchLearningTargets();
    if (!targets.length) {
      showError('출판사를 선택한 편제가 없습니다. 편제 카드에서 출판사를 먼저 선택해 주세요.');
      setBatchLearningConfirmTargets(null);
      return;
    }
    setBatchLearningBusy(true);
    showSaving('학습내용을 일괄 생성하는 중...');
    try {
      const currentPrefs =
        loadGradeTrackProgramPreferencesCache(workspaceId) ??
        (await getGradeTrackProgramPreferences(workspaceId));
      const nextPublishers: GradeTrackProgramPreferences['learningPublishers'] = {};
      Object.entries(learningPublisherByGrade).forEach(([gradeId, trackMap]) => {
        const nextGrade: Record<string, string> = {};
        Object.entries(trackMap ?? {}).forEach(([trackName, publisher]) => {
          const tn = trackName.trim();
          const pub = (publisher ?? '').trim();
          if (tn && pub) nextGrade[tn] = pub;
        });
        if (Object.keys(nextGrade).length > 0) nextPublishers[gradeId] = nextGrade;
      });
      await saveGradeTrackProgramPreferences(workspaceId, {
        ...currentPrefs,
        learningPublishers: nextPublishers,
      });
      const uniqueGradeIds = Array.from(new Set(targets.map((t) => t.gradeId)));
      const fallbackYear = new Date().getFullYear();
      const academicYearByGradeId: Record<string, number> = {};
      const gradeNumberByGradeId: Record<string, 1 | 2 | 3 | 4 | 5 | 6> = {};
      await Promise.all(
        uniqueGradeIds.map(async (gradeId) => {
          academicYearByGradeId[gradeId] = await resolveCalendarLinkedAcademicYear(
            workspaceId,
            gradeId,
            fallbackYear
          );
          const grade = grades.find((g) => g.id === gradeId);
          gradeNumberByGradeId[gradeId] = lowestGradeNumberFromClassName(grade?.name ?? '');
        })
      );
      const result = await generateProgramLearningFromSelectedPublishers({
        workspaceId,
        academicYearByGradeId,
        gradeNumberByGradeId,
        targets,
      });
      clearToast();
      setBatchLearningConfirmTargets(null);
      setBatchLearningResult(result);
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : '학습내용 일괄 생성에 실패했습니다.');
    } finally {
      setBatchLearningBusy(false);
    }
  }, [
    batchLearningConfirmTargets,
    clearToast,
    collectBatchLearningTargets,
    grades,
    learningPublisherByGrade,
    showError,
    showSaving,
    workspaceId,
  ]);

  const classLabelForSection = useCallback(
    (section: TrackSection): string => {
      const gradeId = section.gradeIds[0] ?? '';
      const grade = grades.find((g) => g.id === gradeId);
      return grade ? parseClassName(grade.name).title : '클래스';
    },
    [grades]
  );

  const buildClipboardColumnsForSection = useCallback(
    (section: TrackSection): GradeTrackSectionClipboardColumn[] => {
      const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
      const gradeIds = section.gradeIds;
      const sectionTracks = sourceGradeId
        ? tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId)
        : [];
      const orderedNames = sourceGradeId ? getOrderedTrackNames([sourceGradeId]) : [];
      const deletedTrackIdsForSection = new Set(
        pendingTrackGroupDeletes
          .filter((p) => p.sectionId === section.id)
          .map((p) => p.group[0]?.id)
          .filter((id): id is string => Boolean(id))
      );
      const pendingForSection = pendingAdds
        .map((p, globalIndex) => (p.sectionId === section.id ? { p, globalIndex } : null))
        .filter((x): x is { p: (typeof pendingAdds)[0]; globalIndex: number } => x != null);
      const displayOrder = buildSectionDisplayColumnIds({
        sectionTracks,
        orderedNames,
        pendingForSection,
        deletedTrackIds: deletedTrackIdsForSection,
        savedOrder: sectionColumnOrder[section.id],
        layoutColumnNames: (section.trackColumns ?? [])
          .map((c) => c.name.trim())
          .filter((n) => n.length > 0),
      });

      const columns: GradeTrackSectionClipboardColumn[] = [];
      displayOrder.forEach((columnId) => {
        const anchor = sectionTracks.find((t) => t.id === columnId);
        const serverGroup = anchor ? sectionTracks.filter((t) => t.name === anchor.name) : [];
        if (serverGroup.length > 0) {
          const key = `${section.id}-${serverGroup[0].id}`;
          const displayTrackName = draftEdits[key]?.name?.trim() || serverGroup[0].name;
          const aliases = uniqueNonEmptyAliases(
            draftEdits[key]?.aliases?.length
              ? draftEdits[key].aliases
              : serverGroup.map((t) => (t.description ?? '').trim()).filter(Boolean),
            displayTrackName
          );
          const criteriaHours = Math.min(
            999,
            Math.max(
              0,
              parseInt(criteriaHoursByKey[key] ?? String(serverGroup[0].criteria_hours ?? 0), 10) || 0
            )
          );
          columns.push({
            name: displayTrackName,
            aliases,
            criteriaHours,
            wildcardChar:
              sanitizeWildcardChar(
                draftEdits[key]?.wildcardChar ??
                  section.trackColumns?.find((col) => col.name.trim() === displayTrackName.trim())?.wildcardChar ??
                  ''
              ) || undefined,
            trackCategory: consolidatedTrackCategory(gradeIds, displayTrackName, trackCategoryByGrade),
            programMode: consolidatedProgramMode(gradeIds, displayTrackName, trackProgramModeByGrade),
            syncRepAlias: effectiveSyncRepForColumn(
              gradeIds,
              displayTrackName,
              aliases,
              trackAliasOrderByGrade,
              trackProgramSyncRepByGrade
            ),
          });
          return;
        }

        const pendingEntry = pendingForSection.find(({ p }) => p.id === columnId);
        if (!pendingEntry) return;
        const { p, globalIndex } = pendingEntry;
        const name = p.name.trim();
        if (!name) return;
        const aliases = uniqueNonEmptyAliases(p.aliases, name);
        const criteriaHours = Math.min(
          999,
          Math.max(0, parseInt(criteriaHoursByKey[`${section.id}-pending-${globalIndex}`] ?? '0', 10) || 0)
        );
        columns.push({
          name,
          aliases,
          criteriaHours,
          wildcardChar: sanitizeWildcardChar(p.wildcardChar ?? '') || undefined,
          trackCategory: consolidatedTrackCategory(gradeIds, name, trackCategoryByGrade),
          programMode: consolidatedProgramMode(gradeIds, name, trackProgramModeByGrade),
          syncRepAlias: effectiveSyncRepForColumn(
            gradeIds,
            name,
            aliases,
            trackAliasOrderByGrade,
            trackProgramSyncRepByGrade
          ),
        });
      });

      return mergeSectionTrackColumns(columns) as GradeTrackSectionClipboardColumn[];
    },
    [
      criteriaHoursByKey,
      draftEdits,
      getOrderedTrackNames,
      pendingAdds,
      pendingTrackGroupDeletes,
      sectionColumnOrder,
      sectionSourceGradeIdBySection,
      trackAliasOrderByGrade,
      trackCategoryByGrade,
      trackProgramModeByGrade,
      trackProgramSyncRepByGrade,
      tracks,
    ]
  );

  const handleCopySectionData = useCallback(
    (section: TrackSection) => {
      const columns = buildClipboardColumnsForSection(section);
      if (columns.length === 0) {
        showInfo('복사할 편제 데이터가 없습니다.');
        return;
      }
      setCopiedSectionData({
        sourceSectionId: section.id,
        sourceLabel: classLabelForSection(section),
        columns,
      });
      showInfo('해당 섹션의 데이터가 복사되었습니다. 데이터를 붙여넣을 섹션에서 붙여넣기 아이콘을 클릭하세요.');
    },
    [buildClipboardColumnsForSection, classLabelForSection, showInfo]
  );

  /** 섹션 편제·기준 시수만 초기화(등록 클래스는 유지해 빈 입력 틀이 보이게). DB는 저장하기 시에만 반영 */
  const resetSectionData = useCallback(
    (sectionId: string) => {
      const targetSection = sections.find((section) => section.id === sectionId);
      if (!targetSection) {
        setResetSectionContext(null);
        return;
      }
      const gradeIdSet = new Set(targetSection.gradeIds);
      const targetTracks = tracks.filter((t) => gradeIdSet.has(t.grade_id ?? ''));
      const groupsByName = new Map<string, Track[]>();
      targetTracks.forEach((track) => {
        const name = track.name.trim();
        if (!name) return;
        groupsByName.set(name, [...(groupsByName.get(name) ?? []), track]);
      });

      setPendingTrackGroupDeletes((prev) => {
        const existing = new Set(prev.map((entry) => `${entry.sectionId}:${entry.group[0]?.id ?? ''}`));
        const next = [...prev];
        groupsByName.forEach((group) => {
          const key = `${targetSection.id}:${group[0]?.id ?? ''}`;
          if (!existing.has(key)) next.push({ sectionId: targetSection.id, group });
        });
        return next;
      });

      setPendingAdds((prev) => prev.filter((p) => p.sectionId !== targetSection.id));
      setSectionColumnOrder((prev) => {
        const next = { ...prev };
        delete next[targetSection.id];
        return next;
      });
      setDraftEdits((prev) => {
        const next = { ...prev };
        targetTracks.forEach((track) => {
          delete next[`${targetSection.id}-${track.id}`];
        });
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${targetSection.id}-pending-`)) delete next[key];
        });
        return next;
      });
      setCriteriaHoursByKey((prev) => {
        const next = { ...prev };
        targetTracks.forEach((track) => {
          delete next[`${targetSection.id}-${track.id}`];
        });
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${targetSection.id}-pending-`)) delete next[key];
        });
        return next;
      });
      setSectionInputs((prev) => ({ ...prev, [targetSection.id]: { name: '', alias: '' } }));
      setSections((prev) =>
        prev.map((section) =>
          section.id === targetSection.id
            ? { ...section, trackColumns: [] }
            : section
        )
      );
      setResetSectionContext(null);
      showInfo('섹션 데이터가 초기화되었습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
    },
    [sections, showInfo, tracks]
  );

  const applyCopiedSectionDataToTarget = useCallback(() => {
    if (!copiedSectionData || !pasteTargetSectionId) return;
    const targetSection = sections.find((section) => section.id === pasteTargetSectionId);
    if (!targetSection) {
      setPasteTargetSectionId(null);
      return;
    }

    const targetGradeIds = targetSection.gradeIds;
    const sourceGradeId = sectionSourceGradeId(targetSection, sectionSourceGradeIdBySection);
    const targetTracks = sourceGradeId
      ? tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId)
      : [];
    const groupsByName = new Map<string, Track[]>();
    targetTracks.forEach((track) => {
      const name = track.name.trim();
      if (!name) return;
      groupsByName.set(name, [...(groupsByName.get(name) ?? []), track]);
    });

    setPendingTrackGroupDeletes((prev) => {
      const existing = new Set(prev.map((entry) => `${entry.sectionId}:${entry.group[0]?.id ?? ''}`));
      const next = [...prev];
      groupsByName.forEach((group) => {
        const key = `${targetSection.id}:${group[0]?.id ?? ''}`;
        if (!existing.has(key)) next.push({ sectionId: targetSection.id, group });
      });
      return next;
    });

    const copiedColumns = copiedSectionData.columns.map((column) => ({
      ...column,
      id: generateId(),
      aliases: uniqueNonEmptyAliases(column.aliases, column.name),
    }));
    const copiedPendingIds = copiedColumns.map((column) => column.id);
    const remainingPendingAdds = pendingAdds.filter((p) => p.sectionId !== targetSection.id);
    const criteriaPatch: Record<string, string> = {};
    copiedColumns.forEach((column, index) => {
      criteriaPatch[`${targetSection.id}-pending-${remainingPendingAdds.length + index}`] = String(
        Math.min(999, Math.max(0, column.criteriaHours ?? 0))
      );
    });

    setPendingAdds([
      ...remainingPendingAdds,
      ...copiedColumns.map((column) => ({
        sectionId: targetSection.id,
        gradeIds: targetGradeIds,
        name: column.name.trim(),
        aliases: uniqueNonEmptyAliases(column.aliases, column.name),
        wildcardChar: sanitizeWildcardChar(column.wildcardChar ?? '') || undefined,
        id: column.id,
      })),
    ]);
    setSectionColumnOrder((prev) => ({ ...prev, [targetSection.id]: copiedPendingIds }));
    setDraftEdits((prev) => {
      const next = { ...prev };
      targetTracks.forEach((track) => {
        delete next[`${targetSection.id}-${track.id}`];
      });
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${targetSection.id}-pending-`)) delete next[key];
      });
      return next;
    });
    setCriteriaHoursByKey((prev) => {
      const next = { ...prev };
      targetTracks.forEach((track) => {
        delete next[`${targetSection.id}-${track.id}`];
      });
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${targetSection.id}-pending-`)) delete next[key];
      });
      return { ...next, ...criteriaPatch };
    });

    setTrackAliasOrderByGrade((prev) => {
      const next = { ...prev };
      targetGradeIds.forEach((gradeId) => {
        const map: Record<string, string[]> = {};
        copiedColumns.forEach((column) => {
          const name = column.name.trim();
          if (name) map[name] = uniqueNonEmptyAliases(column.aliases, name);
        });
        next[gradeId] = map;
      });
      return next;
    });
    setTrackProgramModeByGrade((prev) => {
      const next = { ...prev };
      targetGradeIds.forEach((gradeId) => {
        const map: Record<string, TrackProgramMode> = {};
        copiedColumns.forEach((column) => {
          const name = column.name.trim();
          if (name) map[name] = column.programMode;
        });
        next[gradeId] = map;
      });
      return next;
    });
    setTrackProgramSyncRepByGrade((prev) => {
      const next = { ...prev };
      targetGradeIds.forEach((gradeId) => {
        const map: Record<string, string> = {};
        copiedColumns.forEach((column) => {
          const name = column.name.trim();
          const rep = (column.syncRepAlias ?? '').trim();
          if (name && column.programMode === 'sync' && rep) map[name] = rep;
        });
        next[gradeId] = map;
      });
      return next;
    });
    setTrackCategoryByGrade((prev) => {
      const next = { ...prev };
      targetGradeIds.forEach((gradeId) => {
        const map: Record<string, TrackCategory> = {};
        copiedColumns.forEach((column) => {
          const name = column.name.trim();
          if (name) map[name] = column.trackCategory === 'other' ? 'other' : 'regular';
        });
        next[gradeId] = map;
      });
      return next;
    });

    setPasteTargetSectionId(null);
    showInfo('복사한 편제 데이터가 붙여넣어졌습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
  }, [
    copiedSectionData,
    pasteTargetSectionId,
    pendingAdds,
    sectionSourceGradeIdBySection,
    sections,
    showInfo,
    tracks,
  ]);

  /** 섹션의 현재 편제 블럭 이름(서버 그룹 − 삭제 예정 + 저장 전 추가) */
  const sectionBlockNames = useCallback(
    (section: TrackSection): { serverGroups: Map<string, Track[]>; names: string[] } => {
      const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
      const deleted = new Set(
        pendingTrackGroupDeletes.filter((p) => p.sectionId === section.id).map((p) => p.group[0]?.name?.trim() ?? '')
      );
      const serverGroups = new Map<string, Track[]>();
      (sourceGradeId ? tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId) : []).forEach((track) => {
        const name = track.name.trim();
        if (!name || deleted.has(name)) return;
        serverGroups.set(name, [...(serverGroups.get(name) ?? []), track]);
      });
      const names = Array.from(serverGroups.keys());
      pendingAdds.forEach((p) => {
        if (p.sectionId === section.id && p.name.trim() && !names.includes(p.name.trim())) names.push(p.name.trim());
      });
      return { serverGroups, names };
    },
    [pendingAdds, pendingTrackGroupDeletes, sectionSourceGradeIdBySection, tracks]
  );

  const openPresetLoad = useCallback(
    (sectionId: string) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      if (section.gradeIds.length === 0) {
        showError('현재 섹션에 연결된 클래스가 없습니다. 클래스를 등록한 뒤 다시 시도해 주세요.');
        return;
      }
      const fromClass = Array.from(new Set(gradeNumbersFromGradeIds(section.gradeIds, grades))).sort();
      const known = fromClass.length > 0 && fromClass.length < 6;
      setPresetLoadContext({
        sectionId,
        gradeOptions: [1, 2, 3, 4, 5, 6],
        /** 클래스 학년 기본 선택(여러 학년이면 가장 낮은 학년, 알 수 없으면 1학년) */
        grade: known ? fromClass[0] : 1,
        existingNames: sectionBlockNames(section).names,
        keepUnmatched: false,
      });
    },
    [grades, sectionBlockNames, sections, showError]
  );

  /**
   * 프리셋을 섹션에 반영(등록·붙여넣기와 같은 로컬 초안 경로, DB 는 '저장' 시).
   * 같은 이름의 기존 블럭은 삭제하지 않고 기준시수·학기 구분만 바꾸고 시간표명은 없으면 추가(→ 프로그램 데이터 보존).
   * 프리셋에 없는 기존 블럭은 keepUnmatched=false 이면 삭제 예정(저장 시 기존 블럭 삭제와 동일하게 처리).
   */
  const applyPresetToSection = useCallback(
    (sectionId: string, gradeNum: 1 | 2 | 3 | 4 | 5 | 6, keepUnmatched: boolean) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      const preset = DEFAULT_SUBJECT_PRESETS[gradeNum];
      const presetNames = new Set(preset.map((r) => r.name));
      const targetGradeIds = section.gradeIds;
      const { serverGroups } = sectionBlockNames(section);

      if (!keepUnmatched) {
        setPendingTrackGroupDeletes((prev) => {
          const existing = new Set(prev.map((e) => `${e.sectionId}:${e.group[0]?.id ?? ''}`));
          const next = [...prev];
          serverGroups.forEach((group, name) => {
            if (presetNames.has(name)) return;
            const key = `${section.id}:${group[0]?.id ?? ''}`;
            if (!existing.has(key)) next.push({ sectionId: section.id, group });
          });
          return next;
        });
      }

      const keptPending = pendingAdds.filter(
        (p) => p.sectionId === section.id && keepUnmatched && !presetNames.has(p.name.trim())
      );
      const otherPending = pendingAdds.filter((p) => p.sectionId !== section.id);
      const remainingPending = [...otherPending, ...keptPending];
      const newRows = preset.filter((r) => !serverGroups.has(r.name));
      /** 신규 프리셋 블럭에 기본 와일드카드(편제명 첫 글자) — 섹션 내 이미 쓰인 글자면 비워 둠 */
      const usedWildcards = new Set<string>();
      Array.from(serverGroups.keys()).forEach((name) => {
        const key = `${section.id}-${serverGroups.get(name)![0].id}`;
        const w = sanitizeWildcardChar(
          draftEdits[key]?.wildcardChar ??
            section.trackColumns?.find((col) => col.name.trim() === name.trim())?.wildcardChar ??
            ''
        );
        if (w) usedWildcards.add(w);
      });
      keptPending.forEach((p) => {
        const w = sanitizeWildcardChar(p.wildcardChar ?? '');
        if (w) usedWildcards.add(w);
      });
      const newPending = newRows.map((r) => {
        const def = defaultWildcardFromName(r.name);
        const wildcardChar = def && !usedWildcards.has(def) ? def : undefined;
        if (wildcardChar) usedWildcards.add(wildcardChar);
        return {
          sectionId: section.id,
          gradeIds: targetGradeIds,
          name: r.name,
          aliases: [r.alias],
          wildcardChar,
          id: generateId(),
        };
      });
      setPendingAdds([...remainingPending, ...newPending]);

      setDraftEdits((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${section.id}-pending-`)) delete next[key];
        });
        preset.forEach((r) => {
          const group = serverGroups.get(r.name);
          if (!group) return;
          const key = `${section.id}-${group[0].id}`;
          const base = next[key]?.aliases ?? group.map((t) => (t.description ?? '').trim()).filter(Boolean);
          const aliases = base.length ? base : [r.name];
          next[key] = {
            ...(next[key] ?? {}),
            name: r.name,
            aliases: aliases.includes(r.alias) ? aliases : [...aliases, r.alias],
            wildcardChar: next[key]?.wildcardChar,
          };
        });
        return next;
      });

      setCriteriaHoursByKey((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${section.id}-pending-`)) delete next[key];
        });
        preset.forEach((r) => {
          const group = serverGroups.get(r.name);
          if (group) group.forEach((t) => (next[`${section.id}-${t.id}`] = String(r.criteriaHours)));
        });
        newRows.forEach((r, i) => {
          next[`${section.id}-pending-${remainingPending.length + i}`] = String(r.criteriaHours);
        });
        return next;
      });

      setSectionColumnOrder((prev) => {
        const order = preset.map((r) => (serverGroups.has(r.name) ? r.name : newPending.find((p) => p.name === r.name)!.id));
        if (keepUnmatched) {
          (prev[section.id] ?? []).forEach((k) => {
            if (!order.includes(k) && !presetNames.has(k)) order.push(k);
          });
          keptPending.forEach((p) => {
            if (!order.includes(p.id)) order.push(p.id);
          });
        }
        return { ...prev, [section.id]: order };
      });

      setTrackCategoryByGrade((prev) => {
        const next = { ...prev };
        targetGradeIds.forEach((gid) => {
          const map = { ...(next[gid] ?? {}) };
          preset.forEach((r) => (map[r.name] = 'regular'));
          next[gid] = map;
        });
        return next;
      });
      setHoursAdjustByGrade((prev) => {
        const next = { ...prev };
        targetGradeIds.forEach((gid) => {
          const map = { ...(next[gid] ?? {}) };
          preset.forEach((r) => {
            map[r.name] = { ...getTrackHoursAdjustSettings(map, r.name), termScope: r.termScope };
          });
          next[gid] = map;
        });
        return next;
      });

      setPresetLoadContext(null);
      showInfo(`${gradeNum}학년 기본 편제를 불러왔습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.`);
    },
    [
      draftEdits,
      pendingAdds,
      sectionBlockNames,
      sections,
      setCriteriaHoursByKey,
      setDraftEdits,
      setHoursAdjustByGrade,
      setPendingAdds,
      setPendingTrackGroupDeletes,
      setSectionColumnOrder,
      setTrackCategoryByGrade,
      showInfo,
    ]
  );

  const requestPresetLoad = useCallback(
    (sectionId: string) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      const fromClass = Array.from(new Set(gradeNumbersFromGradeIds(section.gradeIds, grades)));
      const single = fromClass.length === 1 ? fromClass[0] : null;
      if (single && sectionBlockNames(section).names.length === 0 && section.gradeIds.length > 0) {
        applyPresetToSection(sectionId, single, false);
        return;
      }
      openPresetLoad(sectionId);
    },
    [applyPresetToSection, grades, openPresetLoad, sectionBlockNames, sections]
  );

  /** 팀장 편제 가져오기 — UI만 반영, DB는 저장하기 시 */
  const applyLeaderGradeTrackImport = useCallback(
    (targetSectionId: string, payload: GradeTrackLeaderImportLocalPayload) => {
      const targetSection = sections.find((section) => section.id === targetSectionId);
      if (!targetSection) return;
      const targetGradeIds = targetSection.gradeIds;
      if (targetGradeIds.length === 0) {
        showError('현재 섹션에 연결된 클래스가 없습니다. 클래스를 등록한 뒤 다시 시도해 주세요.');
        return;
      }
      const sourceSlots = payload.sourceSection.grade_slots;
      if (sourceSlots.length === 0 && payload.sourceSection.track_columns.length === 0) {
        showError('팀장 섹션에 가져올 편제가 없습니다.');
        return;
      }
      const mappedCount = Math.min(sourceSlots.length || 1, targetGradeIds.length);
      if (mappedCount <= 0) {
        showError('가져올 수 있는 클래스 슬롯이 없습니다.');
        return;
      }
      const mappedGradeIds = targetGradeIds.slice(0, mappedCount);
      const primarySlot = sourceSlots[0];
      const columnNames = (() => {
        const fromColumns = payload.sourceSection.track_columns
          .map((c) => c.name.trim())
          .filter((n) => n.length > 0);
        if (fromColumns.length > 0) return fromColumns;
        if (!primarySlot) return [] as string[];
        return Array.from(
          new Set(primarySlot.tracks.map((t) => t.name.trim()).filter((n) => n.length > 0))
        );
      })();
      if (columnNames.length === 0) {
        showError('팀장 섹션에 가져올 편제가 없습니다.');
        return;
      }

      const buildColumnsForSlot = (
        slot: (typeof sourceSlots)[number] | undefined
      ): GradeTrackSectionClipboardColumn[] => {
        return columnNames.map((name) => {
          const group = (slot?.tracks ?? []).filter((t) => t.name.trim() === name);
          const aliases = uniqueNonEmptyAliases(
            group.map((t) => (t.description ?? '').trim()).filter(Boolean),
            name
          );
          const meta = resolveLeaderPreviewTrackMeta(name, aliases, {
            slotMeta: slot?.track_meta?.[name],
            trackColumns: payload.sourceSection.track_columns,
            programPreferences: payload.programPreferences,
            gradeId: slot?.grade_id,
          });
          const criteriaHours = Math.min(
            999,
            Math.max(0, Number(group[0]?.criteria_hours ?? 0) || 0)
          );
          const trackCategory: TrackCategory = group.some((t) => t.track_category === 'other')
            ? 'other'
            : 'regular';
          return {
            id: generateId(),
            name,
            aliases,
            criteriaHours,
            trackCategory,
            wildcardChar: sanitizeWildcardChar(meta.wildcardChar) || undefined,
            programMode: meta.programMode,
            syncRepAlias: meta.syncRepAlias,
          };
        });
      };

      /** 화면·저장 컬럼은 1번째 슬롯(섹션 레이아웃) 기준 — 복사/붙여넣기와 동일 */
      const importedColumns = buildColumnsForSlot(primarySlot);
      const sourceGradeId = sectionSourceGradeId(targetSection, sectionSourceGradeIdBySection);
      const targetTracks = sourceGradeId
        ? tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId)
        : [];
      const groupsByName = new Map<string, Track[]>();
      targetTracks.forEach((track) => {
        const name = track.name.trim();
        if (!name) return;
        groupsByName.set(name, [...(groupsByName.get(name) ?? []), track]);
      });

      setPendingTrackGroupDeletes((prev) => {
        const existing = new Set(prev.map((entry) => `${entry.sectionId}:${entry.group[0]?.id ?? ''}`));
        const next = [...prev];
        groupsByName.forEach((group) => {
          const key = `${targetSection.id}:${group[0]?.id ?? ''}`;
          if (!existing.has(key)) next.push({ sectionId: targetSection.id, group });
        });
        return next;
      });

      const importedPendingIds = importedColumns.map((column) => column.id as string);
      const remainingPendingAdds = pendingAdds.filter((p) => p.sectionId !== targetSection.id);
      const criteriaPatch: Record<string, string> = {};
      importedColumns.forEach((column, index) => {
        criteriaPatch[`${targetSection.id}-pending-${remainingPendingAdds.length + index}`] = String(
          Math.min(999, Math.max(0, column.criteriaHours ?? 0))
        );
      });

      setPendingAdds([
        ...remainingPendingAdds,
        ...importedColumns.map((column) => ({
          sectionId: targetSection.id,
          gradeIds: mappedGradeIds,
          name: column.name.trim(),
          aliases: uniqueNonEmptyAliases(column.aliases, column.name),
          wildcardChar: sanitizeWildcardChar(column.wildcardChar ?? '') || undefined,
          id: (column.id as string) || generateId(),
        })),
      ]);
      setSectionColumnOrder((prev) => ({ ...prev, [targetSection.id]: importedPendingIds }));
      setDraftEdits((prev) => {
        const next = { ...prev };
        targetTracks.forEach((track) => {
          delete next[`${targetSection.id}-${track.id}`];
        });
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${targetSection.id}-pending-`)) delete next[key];
        });
        return next;
      });
      setCriteriaHoursByKey((prev) => {
        const next = { ...prev };
        targetTracks.forEach((track) => {
          delete next[`${targetSection.id}-${track.id}`];
        });
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${targetSection.id}-pending-`)) delete next[key];
        });
        return { ...next, ...criteriaPatch };
      });

      setTrackAliasOrderByGrade((prev) => {
        const next = { ...prev };
        for (let i = 0; i < mappedCount; i += 1) {
          const gradeId = mappedGradeIds[i];
          if (!gradeId) continue;
          const slot = sourceSlots[i] ?? primarySlot;
          const slotColumns = buildColumnsForSlot(slot);
          const map: Record<string, string[]> = {};
          slotColumns.forEach((column) => {
            const name = column.name.trim();
            if (name) map[name] = uniqueNonEmptyAliases(column.aliases, name);
          });
          next[gradeId] = map;
        }
        return next;
      });
      setTrackProgramModeByGrade((prev) => {
        const next = { ...prev };
        for (let i = 0; i < mappedCount; i += 1) {
          const gradeId = mappedGradeIds[i];
          if (!gradeId) continue;
          const slot = sourceSlots[i] ?? primarySlot;
          const slotColumns = buildColumnsForSlot(slot);
          const map: Record<string, TrackProgramMode> = {};
          slotColumns.forEach((column) => {
            const name = column.name.trim();
            if (name) map[name] = column.programMode;
          });
          next[gradeId] = map;
        }
        return next;
      });
      setTrackProgramSyncRepByGrade((prev) => {
        const next = { ...prev };
        for (let i = 0; i < mappedCount; i += 1) {
          const gradeId = mappedGradeIds[i];
          if (!gradeId) continue;
          const slot = sourceSlots[i] ?? primarySlot;
          const slotColumns = buildColumnsForSlot(slot);
          const map: Record<string, string> = {};
          slotColumns.forEach((column) => {
            const name = column.name.trim();
            const rep = (column.syncRepAlias ?? '').trim();
            if (name && column.programMode === 'sync' && rep) map[name] = rep;
          });
          next[gradeId] = map;
        }
        return next;
      });
      setTrackCategoryByGrade((prev) => {
        const next = { ...prev };
        for (let i = 0; i < mappedCount; i += 1) {
          const gradeId = mappedGradeIds[i];
          if (!gradeId) continue;
          const slot = sourceSlots[i] ?? primarySlot;
          const slotColumns = buildColumnsForSlot(slot);
          const map: Record<string, TrackCategory> = {};
          slotColumns.forEach((column) => {
            const name = column.name.trim();
            if (name) map[name] = column.trackCategory === 'other' ? 'other' : 'regular';
          });
          next[gradeId] = map;
        }
        return next;
      });

      setSections((prev) =>
        prev.map((section) =>
          section.id === targetSection.id
            ? {
                ...section,
                trackColumns: importedColumns.map((column) => ({
                  id: column.id,
                  name: column.name,
                  aliases: column.aliases,
                  criteriaHours: column.criteriaHours,
                  trackCategory: column.trackCategory,
                  wildcardChar: column.wildcardChar,
                })),
              }
            : section
        )
      );

      setLeaderImportTargetSectionId(null);
      showInfo('팀장 편제를 가져왔습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
    },
    [
      pendingAdds,
      sectionSourceGradeIdBySection,
      sections,
      showError,
      showInfo,
      tracks,
    ]
  );

  const [aiSectionId, setAiSectionId] = React.useState<string | null>(null);
  /** 섹션에 이미 있는 편제명(서버 편제 + 저장 전 추가분) */
  const aiExistingTrackNames = (sectionId: string): string[] => {
    const section = sections.find((x) => x.id === sectionId);
    if (!section) return [];
    const src = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
    const names = new Set<string>();
    if (src) tracks.filter((t) => (t.grade_id ?? '') === src).forEach((t) => t.name.trim() && names.add(t.name.trim()));
    (section.trackColumns ?? []).forEach((c) => (c.name ?? '').trim() && names.add((c.name ?? '').trim()));
    pendingAdds.filter((p) => p.sectionId === sectionId).forEach((p) => p.name.trim() && names.add(p.name.trim()));
    return Array.from(names);
  };
  /** AI 자료 입력 반영: 같은 이름 편제는 그대로 두고(덮어쓰지 않음) 새 이름만 추가 — 생성 카드 추가와 같은 로컬 초안 경로 */
  const applyAiTracks = (sectionId: string, rows: AiTrackRow[]) => {
    const section = sections.find((x) => x.id === sectionId);
    if (!section) return;
    const ids = section.gradeIds;
    if (ids.length === 0) {
      showError('현재 섹션에 연결된 클래스가 없습니다. 클래스를 등록한 뒤 다시 시도해 주세요.');
      return;
    }
    const norm = (v: string) => v.replace(/\s+/g, '');
    const existing = new Set(aiExistingTrackNames(sectionId).map(norm));
    const seen = new Set<string>();
    const fresh = rows.filter((t) => {
      const k = norm(t.name);
      if (!k || existing.has(k) || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const kept = rows.length - fresh.length;
    if (fresh.length === 0) {
      showInfo(`새로 추가할 편제가 없습니다. 기존 편제 ${kept}개는 그대로 두었어요.`);
      return;
    }
    const base = pendingAdds.length;
    const added = fresh.map((t) => ({
      sectionId,
      gradeIds: ids,
      name: t.name.trim(),
      aliases: uniqueNonEmptyAliases([(t.short_name || t.name).trim()], t.name.trim()),
      wildcardChar: undefined,
      id: generateId(),
    }));
    setPendingAdds((prev) => [...prev, ...added]);
    setSectionColumnOrder((prev) => ({
      ...prev,
      [sectionId]: dedupeStrings([...(prev[sectionId] ?? []), ...added.map((x) => x.id)]),
    }));
    setCriteriaHoursByKey((prev) => {
      const next = { ...prev };
      fresh.forEach((t, i) => {
        next[`${sectionId}-pending-${base + i}`] = String(Math.min(999, Math.max(0, Number(t.criteria_hours) || 0)));
      });
      return next;
    });
    fresh.forEach((t) => {
      const name = t.name.trim();
      setTermScopeForGrades(ids, name, t.term_scope);
      setTrackCategoryForGrades(ids, name, t.category);
      if (t.publisher.trim()) setLearningPublisherForGrades(ids, name, t.publisher);
    });
    showInfo(
      `편제 ${fresh.length}개를 새로 추가했어요${kept ? ` (기존 ${kept}개는 그대로 유지)` : ''}. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.`
    );
  };
  const aiDefaultGradeFor = (sectionId: string): number => {
    const ids = sections.find((x) => x.id === sectionId)?.gradeIds ?? [];
    const gid = ids.includes(currentGradeId) ? currentGradeId : ids[0];
    const g = grades.find((x) => x.id === gid) ?? grades.find((x) => x.id === currentGradeId);
    return gradeNumbersFromClassName(g?.name ?? '')[0] ?? 1;
  };
  const presetTermScope = (grade: number, name: string): 'split' | 'unified' | null => {
    const norm = (v: string) => v.replace(/\s+/g, '');
    const list = DEFAULT_SUBJECT_PRESETS[(Math.min(6, Math.max(1, grade)) as 1 | 2 | 3 | 4 | 5 | 6)] ?? [];
    return list.find((p) => norm(p.name) === norm(name) || norm(p.alias) === norm(name))?.termScope ?? null;
  };

  const handleTrackDragEnd = (
    gradeIds: string[],
    sectionId: string,
    displayOrder: string[]
  ) => (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = displayOrder.indexOf(active.id as string);
    const newIndex = displayOrder.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const nextOrder = arrayMove(displayOrder, oldIndex, newIndex);
    setSectionColumnOrder((prev) => ({ ...prev, [sectionId]: nextOrder }));
    /* 순서는 저장하기 버튼으로만 반영. 드래그만으로는 localStorage/trackOrderByGrade 갱신 안 함 */
  };

  const visibleSections =
    currentGradeId === ''
      ? sections
      : sections.filter((section) => section.gradeIds.includes(currentGradeId));

  return (
    <section className="cp-timetable-hub-page">
      <div className="cp-page-title-row">
        <div>
          <h2>편제 및 기준 시수</h2>
        </div>
        <div className="cp-grade-track-title-actions">
          <button
            type="button"
            className="cp-btn-secondary"
            onClick={openBatchGenerateConfirm}
            disabled={loading || batchLearningBusy || grades.length === 0}
          >
            {batchLearningBusy ? '학습내용 생성 중...' : '학습내용 일괄 생성'}
          </button>
          <button
            type="button"
            className="cp-btn-save-all"
            onClick={handleSaveAll}
            disabled={loading || batchLearningBusy || !isDirty}
          >
            {isDirty ? '저장하기' : '변경사항 없음'}
          </button>
        </div>
      </div>

      {grades.length === 0 ? (
        <article className="cp-card">
          <div className="cp-track-section-header">
            <h3>편제 및 시간표 입력 양식</h3>
          </div>
          <p className="page-desc">
            등록된 클래스가 없습니다. 먼저 클래스를 등록하면 클래스별 편제 입력 블럭이 자동으로 생성됩니다.
          </p>
          {classSettingsModal ? (
            <button
              type="button"
              className="cp-btn-secondary"
              onClick={() => classSettingsModal.openClassSettingsModal()}
            >
              클래스 등록 바로가기
            </button>
          ) : null}
        </article>
      ) : visibleSections.length === 0 ? (
        <article className="cp-card">
          <div className="cp-track-section-header">
            <h3>편제 및 시간표 입력 양식</h3>
          </div>
          <p className="page-desc">
            선택한 클래스의 편제 섹션을 준비하는 중이거나, 아직 이 클래스용 섹션이 없습니다. 잠시 후
            다시 확인하거나 상단에서 다른 클래스를 선택해 주세요.
          </p>
        </article>
      ) : (
        <>
            {visibleSections.map((section) => {
              const gradeIds = section.gradeIds;
              const visibleGradeIds =
                currentGradeId === '' ? gradeIds : gradeIds.filter((id) => id === currentGradeId);
              const sourceGradeId = sectionSourceGradeId(section, sectionSourceGradeIdBySection);
              const learningGradeNumbers = gradeNumbersFromGradeIds(
                visibleGradeIds.length ? visibleGradeIds : gradeIds,
                grades
              );
              const learningClassTitle = (() => {
                const ids = visibleGradeIds.length ? visibleGradeIds : gradeIds;
                const grade = grades.find((g) => g.id === ids[0]);
                return grade ? parseClassName(grade.name).title : '';
              })();
              const sectionTracks = sourceGradeId
                ? tracks.filter((t) => (t.grade_id ?? '') === sourceGradeId)
                : [];
              const orderedNames = sourceGradeId ? getOrderedTrackNames([sourceGradeId]) : [];
              const deletedTrackIdsForSection = new Set(
                pendingTrackGroupDeletes
                  .filter((p) => p.sectionId === section.id)
                  .map((p) => p.group[0]?.id)
                  .filter((id): id is string => Boolean(id))
              );
              const pendingForSection = pendingAdds
                .map((p, globalIndex) => (p.sectionId === section.id ? { p, globalIndex } : null))
                .filter((x): x is { p: (typeof pendingAdds)[0]; globalIndex: number } => x != null);
              const displayOrder = buildSectionDisplayColumnIds({
                sectionTracks,
                orderedNames,
                pendingForSection,
                deletedTrackIds: deletedTrackIdsForSection,
                savedOrder: sectionColumnOrder[section.id],
                layoutColumnNames: (section.trackColumns ?? [])
                  .map((c) => c.name.trim())
                  .filter((n) => n.length > 0),
              });

              return (
                    <article key={section.id} className="cp-card">
                      <div className="cp-track-section-header cp-track-section-header-with-class cp-grade-track-header-row cp-section-mhead">
                        <div className="card-summary pill cp-track-class-pill">
                          클래스 단독
                        </div>
                        <h3>편제 및 시간표 입력 양식</h3>
                        {visibleGradeIds.map((gid) => {
                          const grade = grades.find((g) => g.id === gid);
                          const parsed = grade ? parseClassName(grade.name) : { title: gid, grades: [] };
                          return (
                            <div key={gid} className="cp-grade-track-class-block">
                              <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                              {parsed.grades.map((num) => (
                                <span key={num} className="class-grade-chip-display">
                                  {num}
                                </span>
                              ))}
                            </div>
                          );
                        })}
                        <div className="cp-grade-track-section-header-actions">
                          <AiPresetOpenButton onClick={() => setAiSectionId(section.id)} />
                          <button
                            type="button"
                            className="cp-program-color-section-copy-btn"
                            onClick={() => setResetSectionContext(section.id)}
                            aria-label="섹션 초기화"
                            title="섹션 초기화"
                          >
                            <img src="/reset.svg" alt="" className="track-icon" />
                          </button>
                          <button
                            type="button"
                            className="cp-program-color-section-copy-btn"
                            onClick={() => handleCopySectionData(section)}
                            aria-label="섹션 데이터 복사"
                            title="섹션 데이터 복사"
                          >
                            <img src="/Copy.svg" alt="복사" className="track-icon" />
                          </button>
                          <button
                            type="button"
                            className="cp-program-color-section-copy-btn"
                            onClick={() => {
                              if (!copiedSectionData) {
                                showInfo('먼저 복사할 섹션의 복사 아이콘을 클릭해 주세요.');
                                return;
                              }
                              setPasteTargetSectionId(section.id);
                            }}
                            aria-label="섹션 데이터 붙여넣기"
                            title="섹션 데이터 붙여넣기"
                          >
                            <img src="/paste.svg" alt="붙여넣기" className="track-icon" />
                          </button>
                          {teamIdForLeaderImport ? (
                            <button
                              type="button"
                              className="cp-program-color-section-copy-btn"
                              onClick={() => setLeaderImportTargetSectionId(section.id)}
                              aria-label="팀장 편제 가져오기"
                              title="팀장 편제 가져오기"
                            >
                              <img src="/Download.svg" alt="" className="track-icon" />
                            </button>
                          ) : null}
                        </div>
                      </div>

                <div className="cp-section-body">
                  <div className="cp-grade-track-toolbar">
                    <button
                      type="button"
                      className="cp-btn-secondary cp-grade-track-preset-btn"
                      onClick={() => requestPresetLoad(section.id)}
                      disabled={loading}
                      title="학년별 기본 편제(정규 교과·기준시수)를 불러옵니다"
                    >
                      기본 편제 예시 불러오기
                    </button>
                  </div>
                  <p className="page-desc cp-grade-track-wildcard-hint">
                    💡 와일드카드( ) 입력 안내
                    <br />
                    시간표 과목명 뒤에 괄호와 함께 지정된 문자를 입력하면, 해당 과목들이 하나의 상위 편제로
                    통합되어 카운팅됩니다.
                    <br />
                    (예: 음악의 와일드카드를 &apos;음&apos;으로 지정 시 ➔ 전담(음), 밴드(음) 모두
                    &apos;음악&apos;으로 계산됨)
                  </p>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleTrackDragEnd(visibleGradeIds, section.id, displayOrder)}
                  >
                    <SortableContext
                      items={displayOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      <div className="track-table">
                        {displayOrder.map((columnId) => {
                          const anchor = sectionTracks.find((t) => t.id === columnId);
                          const serverGroup = anchor
                            ? sectionTracks.filter((t) => t.name === anchor.name)
                            : [];
                          if (serverGroup.length > 0) {
                            const serverTrackKey = `${section.id}-${serverGroup[0].id}`;
                            const displayTrackName =
                              draftEdits[serverTrackKey]?.name?.trim() || serverGroup[0].name;
                            const wildcardChar = sanitizeWildcardChar(
                              draftEdits[serverTrackKey]?.wildcardChar ??
                                section.trackColumns?.find((col) => col.name.trim() === displayTrackName.trim())
                                  ?.wildcardChar ??
                                ''
                            );
                            const rawAliases = serverGroup
                              .map((t) => (t.description ?? '').trim())
                              .filter((a) => a.length > 0);
                            const layoutAliases =
                              section.trackColumns?.find((col) => col.name.trim() === displayTrackName.trim())
                                ?.aliases ?? [];
                            const orderedAliases = buildOrderedAliasesForTrack(
                              sourceGradeId || gradeIds[0] || '',
                              displayTrackName,
                              rawAliases.length > 0
                                ? rawAliases
                                : layoutAliases.length > 0
                                  ? layoutAliases
                                  : [displayTrackName],
                              trackAliasOrderByGrade
                            );
                            return (
                              <SortableTrackColumn
                                key={`${section.id}-${serverGroup[0].id}`}
                                sortableId={columnId}
                                group={serverGroup}
                                orderedAliases={orderedAliases}
                                criteriaHours={criteriaHoursByKey[serverTrackKey] ?? String(serverGroup[0].criteria_hours ?? 0)}
                                onCriteriaHoursChange={(value) =>
                                  setCriteriaHoursByKey((prev) => ({
                                    ...prev,
                                    [serverTrackKey]: value,
                                  }))
                                }
                                draftEdit={draftEdits[serverTrackKey]}
                                onSaveDraft={(name, aliases) => {
                                  const prevName = displayTrackName;
                                  const prevWildcardChar = wildcardChar;
                                  setDraftEdits((prev) => ({
                                    ...prev,
                                    [serverTrackKey]: {
                                      name,
                                      aliases,
                                      wildcardChar:
                                        sanitizeWildcardChar(
                                          prev[serverTrackKey]?.wildcardChar ?? prevWildcardChar
                                        ) || undefined,
                                    },
                                  }));
                                  setTrackAliasOrderForGrades(gradeIds, name.trim() || prevName, aliases);
                                  const nextName = name.trim();
                                  if (nextName && nextName !== prevName) {
                                    setTrackAliasOrderByGrade((prev) => {
                                      const next = { ...prev };
                                      gradeIds.forEach((gid) => {
                                        const map = { ...(next[gid] ?? {}) };
                                        if (map[prevName]) {
                                          map[nextName] = map[prevName];
                                          delete map[prevName];
                                          next[gid] = map;
                                        }
                                      });
                                      return next;
                                    });
                                    setTrackProgramModeByGrade((prev) => {
                                      const next = { ...prev };
                                      gradeIds.forEach((gid) => {
                                        const map = { ...(next[gid] ?? {}) };
                                        if (map[prevName] !== undefined) {
                                          map[nextName] = map[prevName];
                                          delete map[prevName];
                                          next[gid] = map;
                                        }
                                      });
                                      return next;
                                    });
                                    setTrackProgramSyncRepByGrade((prev) => {
                                      const next = { ...prev };
                                      gradeIds.forEach((gid) => {
                                        const map = { ...(next[gid] ?? {}) };
                                        if (map[prevName] !== undefined) {
                                          map[nextName] = map[prevName];
                                          delete map[prevName];
                                          next[gid] = map;
                                        }
                                      });
                                      return next;
                                    });
                                    setTrackCategoryByGrade((prev) => {
                                      const next = { ...prev };
                                      gradeIds.forEach((gid) => {
                                        const map = { ...(next[gid] ?? {}) };
                                        if (map[prevName] !== undefined) {
                                          map[nextName] = map[prevName];
                                          delete map[prevName];
                                          next[gid] = map;
                                        }
                                      });
                                      return next;
                                    });
                                    setLearningPublisherByGrade((prev) => {
                                      const next = { ...prev };
                                      gradeIds.forEach((gid) => {
                                        const map = { ...(next[gid] ?? {}) };
                                        if (map[prevName] !== undefined) {
                                          map[nextName] = map[prevName];
                                          delete map[prevName];
                                          next[gid] = map;
                                        }
                                      });
                                      return next;
                                    });
                                    setHoursAdjustByGrade((prev) => {
                                      const next = { ...prev };
                                      gradeIds.forEach((gid) => {
                                        const map = { ...(next[gid] ?? {}) };
                                        if (map[prevName] !== undefined) {
                                          map[nextName] = map[prevName];
                                          delete map[prevName];
                                          next[gid] = map;
                                        }
                                      });
                                      return next;
                                    });
                                  }
                                }}
                                onAliasesReorder={(aliases) => {
                                  setDraftEdits((prev) => ({
                                    ...prev,
                                    [serverTrackKey]: {
                                      name: displayTrackName,
                                      aliases,
                                      wildcardChar:
                                        sanitizeWildcardChar(prev[serverTrackKey]?.wildcardChar ?? wildcardChar) ||
                                        undefined,
                                    },
                                  }));
                                  setTrackAliasOrderForGrades(gradeIds, displayTrackName, aliases);
                                }}
                                wildcardChar={wildcardChar}
                                wildcardDuplicate={!!wildcardChar && (wildcardCountsBySection.get(section.id)?.get(wildcardChar) ?? 0) > 1}
                                onWildcardChange={(value) => {
                                  const nextWildcard = sanitizeWildcardChar(value) || undefined;
                                  const serverAliases = (() => {
                                    const cands = serverGroup
                                      .map((t) => (t.description ?? '').trim())
                                      .filter((a) => a.length > 0);
                                    return cands.length > 0 ? cands : [serverGroup[0].name];
                                  })();
                                  const originalWildcard = sanitizeWildcardChar(
                                    section.trackColumns?.find(
                                      (col) => col.name.trim() === displayTrackName.trim()
                                    )?.wildcardChar ?? ''
                                  );
                                  setDraftEdits((prev) => {
                                    const aliases =
                                      prev[serverTrackKey]?.aliases?.length
                                        ? prev[serverTrackKey]!.aliases
                                        : serverAliases;
                                    const name = prev[serverTrackKey]?.name?.trim() || displayTrackName;
                                    const nextDraft = {
                                      name,
                                      aliases,
                                      wildcardChar: nextWildcard,
                                    };
                                    if (
                                      isNoOpDraftEdit(
                                        nextDraft,
                                        serverGroup[0].name,
                                        serverAliases,
                                        originalWildcard
                                      )
                                    ) {
                                      if (!(serverTrackKey in prev)) return prev;
                                      const { [serverTrackKey]: _removed, ...rest } = prev;
                                      return rest;
                                    }
                                    return {
                                      ...prev,
                                      [serverTrackKey]: nextDraft,
                                    };
                                  });
                                }}
                                onRequestDelete={() =>
                                  setPendingDeleteGroup({ sectionId: section.id, group: serverGroup })
                                }
                                programMode={consolidatedProgramMode(
                                  gradeIds,
                                  displayTrackName,
                                  trackProgramModeByGrade
                                )}
                                onProgramModeChange={(mode) => {
                                  const columnAliasesForMode =
                                    draftEdits[serverTrackKey]?.aliases?.length
                                      ? draftEdits[serverTrackKey]!.aliases
                                      : (() => {
                                          const cands = serverGroup
                                            .map((t) => (t.description ?? '').trim())
                                            .filter((a) => a.length > 0);
                                          return cands.length > 0 ? cands : [serverGroup[0].name];
                                        })();
                                  setTrackProgramModeForGrades(
                                    gradeIds,
                                    displayTrackName,
                                    mode,
                                    columnAliasesForMode
                                  );
                                }}
                                syncRepAlias={effectiveSyncRepForColumn(
                                  gradeIds,
                                  displayTrackName,
                                  draftEdits[serverTrackKey]?.aliases?.length
                                    ? draftEdits[serverTrackKey]!.aliases
                                    : (() => {
                                        const cands = serverGroup
                                          .map((t) => (t.description ?? '').trim())
                                          .filter((a) => a.length > 0);
                                        return cands.length > 0 ? cands : [serverGroup[0].name];
                                      })(),
                                  trackAliasOrderByGrade,
                                  trackProgramSyncRepByGrade
                                )}
                                onSyncRepAliasChange={(alias) =>
                                  setTrackProgramSyncRepForGrades(gradeIds, displayTrackName, alias)
                                }
                                trackCategory={consolidatedTrackCategory(
                                  gradeIds,
                                  displayTrackName,
                                  trackCategoryByGrade
                                )}
                                onTrackCategoryToggle={() =>
                                  setTrackCategoryForGrades(
                                    gradeIds,
                                    displayTrackName,
                                    consolidatedTrackCategory(
                                      gradeIds,
                                      displayTrackName,
                                      trackCategoryByGrade
                                    ) === 'other'
                                      ? 'regular'
                                      : 'other'
                                  )
                                }
                                onShowWarning={(msg) => setToastMessage(msg)}
                                learningPublisher={consolidatedLearningPublisher(
                                  gradeIds,
                                  displayTrackName,
                                  learningPublisherByGrade
                                )}
                                onLearningPublisherChange={(publisher) =>
                                  setLearningPublisherForGrades(gradeIds, displayTrackName, publisher)
                                }
                                onPublisherTemplateSelect={(templateName) =>
                                  applyUnifiedScopeIfTemplateUnified(
                                    gradeIds,
                                    displayTrackName,
                                    templateName
                                  )
                                }
                                termScope={consolidatedHoursTermScope(
                                  gradeIds,
                                  displayTrackName,
                                  hoursAdjustByGrade
                                )}
                                onTermScopeChange={(scope) =>
                                  setTermScopeForGrades(gradeIds, displayTrackName, scope)
                                }
                                classTitle={learningClassTitle}
                                learningGradeNumbers={learningGradeNumbers}
                                workspaceId={workspaceId}
                              />
                            );
                          }
                          const pendingEntry = pendingForSection.find(({ p }) => p.id === columnId);
                          if (pendingEntry) {
                            const { p, globalIndex } = pendingEntry;
                            return (
                            <SortablePendingColumn
                              key={`pending-${section.id}-${p.id}`}
                              id={p.id}
                            >
                              <PendingTrackColumn
                                name={p.name}
                                aliases={p.aliases}
                                criteriaHours={criteriaHoursByKey[`${section.id}-pending-${globalIndex}`] ?? '0'}
                                wildcardChar={sanitizeWildcardChar(p.wildcardChar ?? '')}
                                wildcardDuplicate={(() => {
                                  const w = sanitizeWildcardChar(p.wildcardChar ?? '');
                                  return !!w && (wildcardCountsBySection.get(section.id)?.get(w) ?? 0) > 1;
                                })()}
                                onCriteriaHoursChange={(value) =>
                                  setCriteriaHoursByKey((prev) => ({
                                    ...prev,
                                    [`${section.id}-pending-${globalIndex}`]: value,
                                  }))
                                }
                                onWildcardChange={(value) => {
                                  const nextWildcard = sanitizeWildcardChar(value) || undefined;
                                  const prevWildcard = sanitizeWildcardChar(p.wildcardChar ?? '') || undefined;
                                  if (nextWildcard === prevWildcard) return;
                                  setPendingAdds((prev) =>
                                    prev.map((item, i) =>
                                      i === globalIndex
                                        ? { ...item, wildcardChar: nextWildcard }
                                        : item
                                    )
                                  );
                                }}
                                onSaveDraft={(name, aliases) =>
                                  {
                                    const prevName = p.name.trim();
                                    const nextName = name.trim();
                                    setPendingAdds((prev) =>
                                      prev.map((item, i) =>
                                        i === globalIndex
                                          ? {
                                              ...item,
                                              name,
                                              aliases,
                                              wildcardChar:
                                                sanitizeWildcardChar(item.wildcardChar ?? '') || undefined,
                                            }
                                          : item
                                      )
                                    );
                                    setTrackAliasOrderForGrades(gradeIds, nextName || prevName, aliases);
                                    if (nextName && nextName !== prevName) {
                                      setTrackAliasOrderByGrade((prev) => {
                                        const next = { ...prev };
                                        gradeIds.forEach((gid) => {
                                          const map = { ...(next[gid] ?? {}) };
                                          if (map[prevName]) {
                                            map[nextName] = map[prevName];
                                            delete map[prevName];
                                            next[gid] = map;
                                          }
                                        });
                                        return next;
                                      });
                                      setTrackProgramModeByGrade((prev) => {
                                        const next = { ...prev };
                                        gradeIds.forEach((gid) => {
                                          const map = { ...(next[gid] ?? {}) };
                                          if (map[prevName] !== undefined) {
                                            map[nextName] = map[prevName];
                                            delete map[prevName];
                                            next[gid] = map;
                                          }
                                        });
                                        return next;
                                      });
                                      setTrackProgramSyncRepByGrade((prev) => {
                                        const next = { ...prev };
                                        gradeIds.forEach((gid) => {
                                          const map = { ...(next[gid] ?? {}) };
                                          if (map[prevName] !== undefined) {
                                            map[nextName] = map[prevName];
                                            delete map[prevName];
                                            next[gid] = map;
                                          }
                                        });
                                        return next;
                                      });
                                      setTrackCategoryByGrade((prev) => {
                                        const next = { ...prev };
                                        gradeIds.forEach((gid) => {
                                          const map = { ...(next[gid] ?? {}) };
                                          if (map[prevName] !== undefined) {
                                            map[nextName] = map[prevName];
                                            delete map[prevName];
                                            next[gid] = map;
                                          }
                                        });
                                        return next;
                                      });
                                      setLearningPublisherByGrade((prev) => {
                                        const next = { ...prev };
                                        gradeIds.forEach((gid) => {
                                          const map = { ...(next[gid] ?? {}) };
                                          if (map[prevName] !== undefined) {
                                            map[nextName] = map[prevName];
                                            delete map[prevName];
                                            next[gid] = map;
                                          }
                                        });
                                        return next;
                                      });
                                      setHoursAdjustByGrade((prev) => {
                                        const next = { ...prev };
                                        gradeIds.forEach((gid) => {
                                          const map = { ...(next[gid] ?? {}) };
                                          if (map[prevName] !== undefined) {
                                            map[nextName] = map[prevName];
                                            delete map[prevName];
                                            next[gid] = map;
                                          }
                                        });
                                        return next;
                                      });
                                    }
                                  }
                                }
                                onAliasesReorder={(aliases) => {
                                  setPendingAdds((prev) =>
                                    prev.map((item, i) =>
                                      i === globalIndex ? { ...item, aliases } : item
                                    )
                                  );
                                  setTrackAliasOrderForGrades(gradeIds, p.name.trim(), aliases);
                                }}
                                programMode={consolidatedProgramMode(
                                  gradeIds,
                                  p.name.trim(),
                                  trackProgramModeByGrade
                                )}
                                onProgramModeChange={(mode) =>
                                  setTrackProgramModeForGrades(gradeIds, p.name.trim(), mode, p.aliases)
                                }
                                syncRepAlias={effectiveSyncRepForColumn(
                                  gradeIds,
                                  p.name.trim(),
                                  p.aliases,
                                  trackAliasOrderByGrade,
                                  trackProgramSyncRepByGrade
                                )}
                                onSyncRepAliasChange={(alias) =>
                                  setTrackProgramSyncRepForGrades(gradeIds, p.name.trim(), alias)
                                }
                                trackCategory={consolidatedTrackCategory(
                                  gradeIds,
                                  p.name.trim(),
                                  trackCategoryByGrade
                                )}
                                onTrackCategoryToggle={() =>
                                  setTrackCategoryForGrades(
                                    gradeIds,
                                    p.name.trim(),
                                    consolidatedTrackCategory(
                                      gradeIds,
                                      p.name.trim(),
                                      trackCategoryByGrade
                                    ) === 'other'
                                      ? 'regular'
                                      : 'other'
                                  )
                                }
                                onRemove={() => {
                                  setPendingAdds((prev) => prev.filter((_, i) => i !== globalIndex));
                                  setSectionColumnOrder((prev) => ({
                                    ...prev,
                                    [section.id]: (prev[section.id] ?? []).filter((id) => id !== p.id),
                                  }));
                                }}
                                onShowWarning={(msg) => setToastMessage(msg)}
                                learningPublisher={consolidatedLearningPublisher(
                                  gradeIds,
                                  p.name.trim(),
                                  learningPublisherByGrade
                                )}
                                onLearningPublisherChange={(publisher) =>
                                  setLearningPublisherForGrades(gradeIds, p.name.trim(), publisher)
                                }
                                onPublisherTemplateSelect={(templateName) =>
                                  applyUnifiedScopeIfTemplateUnified(
                                    gradeIds,
                                    p.name.trim(),
                                    templateName
                                  )
                                }
                                termScope={consolidatedHoursTermScope(
                                  gradeIds,
                                  p.name.trim(),
                                  hoursAdjustByGrade
                                )}
                                onTermScopeChange={(scope) =>
                                  setTermScopeForGrades(gradeIds, p.name.trim(), scope)
                                }
                                classTitle={learningClassTitle}
                                learningGradeNumbers={learningGradeNumbers}
                                workspaceId={workspaceId}
                                wrapInTrackCol={false}
                              />
                            </SortablePendingColumn>
                            );
                          }
                          return null;
                        })}
                        {creatingTrackSectionId === section.id ? (
                          <TrackCreateCard
                            key={`create-${section.id}`}
                            classTitle={learningClassTitle}
                            learningGradeNumbers={learningGradeNumbers}
                            workspaceId={workspaceId}
                            existingWildcardCounts={wildcardCountsBySection.get(section.id)}
                            onCancel={() => setCreatingTrackSectionId(null)}
                            onShowWarning={(msg) => setToastMessage(msg)}
                            onCreate={(payload) => {
                              if (handleAddTrack(section.id, gradeIds, sectionTracks, payload)) {
                                setCreatingTrackSectionId(null);
                              }
                            }}
                          />
                        ) : (
                          <button
                            type="button"
                            className="track-col track-col-add-card"
                            onClick={() => setCreatingTrackSectionId(section.id)}
                            disabled={loading}
                            aria-label="편제 추가"
                          >
                            <span className="track-col-add-card-plus" aria-hidden="true">+</span>
                            <span className="track-col-add-card-label">편제 추가</span>
                          </button>
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </article>
              );
            })
          }
        </>
      )}

      {aiSectionId ? (
        <AiPresetImportModal
          kind="tracks"
          title="AI 자료 입력 · 편제 및 기준시수"
          resumeKey={`tracks:${workspaceId}:${aiSectionId}`}
          defaultGrade={aiDefaultGradeFor(aiSectionId)}
          schoolYear={guessSchoolYear()}
          presetTermScope={presetTermScope}
          existingTrackNames={aiExistingTrackNames(aiSectionId)}
          onClose={() => setAiSectionId(null)}
          onApply={(rows) => applyAiTracks(aiSectionId, rows)}
        />
      ) : null}

      {leaderImportTargetSectionId && teamIdForLeaderImport ? (
        <GradeTrackLeaderSectionImportModal
          open
          teamId={teamIdForLeaderImport}
          targetWorkspaceId={workspaceId}
          targetSectionId={leaderImportTargetSectionId}
          onClose={() => setLeaderImportTargetSectionId(null)}
          onImported={(payload) => {
            if (!leaderImportTargetSectionId) return;
            applyLeaderGradeTrackImport(leaderImportTargetSectionId, payload);
          }}
        />
      ) : null}

      {resetSectionContext ? (
        <div className="cp-modal-backdrop" onClick={() => setResetSectionContext(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>섹션 초기화</h3>
            <p className="page-desc">
              이 섹션의 편제·기준 시수 데이터를 초기화할까요? 등록된 클래스는 유지되고
              빈 입력 틀이 남습니다. 오른쪽 위 &quot;저장하기&quot;를 눌러야 서버에 반영됩니다.
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setResetSectionContext(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => resetSectionData(resetSectionContext)}
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {presetLoadContext ? (() => {
        const ctx = presetLoadContext;
        const grade = ctx.grade ?? 1;
        const preset = DEFAULT_SUBJECT_PRESETS[grade];
        const presetNames = new Set(preset.map((r) => r.name));
        const removed = ctx.existingNames.filter((n) => !presetNames.has(n));
        const kept = ctx.existingNames.filter((n) => presetNames.has(n));
        const hasExisting = ctx.existingNames.length > 0;
        return (
          <div className="cp-modal-backdrop" onClick={() => setPresetLoadContext(null)}>
            <div className="cp-modal cp-grade-track-preset-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{hasExisting ? '기존 편제 블럭을 덮어쓸까요?' : '기본 편제 예시 불러오기'}</h3>
              <div className="cp-grade-track-preset-body">
                <div className="cp-grade-track-preset-row">
                  <span className="cp-grade-track-preset-label">학년</span>
                  <select
                    className="cp-grade-track-preset-select"
                    value={grade}
                    onChange={(e) =>
                      setPresetLoadContext({ ...ctx, grade: Number(e.target.value) as 1 | 2 | 3 | 4 | 5 | 6 })
                    }
                  >
                    {ctx.gradeOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}학년
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cp-grade-track-preset-chips" aria-label="불러올 편제">
                  {preset.map((r) => (
                    <span key={r.name} className="cp-grade-track-preset-chip">
                      {r.name} <b>{r.criteriaHours}</b>
                    </span>
                  ))}
                </div>
                {kept.length ? (
                  <p className="cp-grade-track-preset-line">같은 이름 {kept.length}개는 유지돼요 (시수·학기만 변경)</p>
                ) : null}
                {removed.length ? (
                  <div className={`cp-grade-track-preset-warn${ctx.keepUnmatched ? ' is-muted' : ''}`}>
                    <div className="cp-grade-track-preset-chips">
                      {removed.map((n) => (
                        <span key={n} className="cp-grade-track-preset-chip is-removed">
                          {n}
                        </span>
                      ))}
                    </div>
                    {!ctx.keepUnmatched ? (
                      <p>예시에 없는 {removed.length}개는 삭제돼요. 저장 시 진도·기록도 함께 삭제됩니다.</p>
                    ) : (
                      <p>예시에 없는 {removed.length}개도 그대로 남겨요.</p>
                    )}
                    <label className="cp-grade-track-preset-check">
                      <input
                        type="checkbox"
                        checked={ctx.keepUnmatched}
                        onChange={(e) => setPresetLoadContext({ ...ctx, keepUnmatched: e.target.checked })}
                      />
                      예시에 없는 블럭 남기기
                    </label>
                  </div>
                ) : null}
              </div>
              <p className="cp-grade-track-preset-foot-note">불러온 뒤 저장해야 반영돼요.</p>
              <div className="cp-modal-footer cp-grade-track-preset-footer">
                <button type="button" className="cp-btn-secondary" onClick={() => setPresetLoadContext(null)}>
                  취소
                </button>
                <button
                  type="button"
                  className={hasExisting ? 'cp-btn-danger' : 'cp-btn-primary'}
                  onClick={() => applyPresetToSection(ctx.sectionId, grade, ctx.keepUnmatched)}
                >
                  {hasExisting ? '덮어쓰기' : '불러오기'}
                </button>
              </div>
            </div>
          </div>
        );
      })() : null}

      {pasteTargetSectionId && copiedSectionData ? (
        <div className="cp-modal-backdrop" onClick={() => setPasteTargetSectionId(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>섹션 데이터 붙여넣기</h3>
            <p className="page-desc">
              &quot;{copiedSectionData.sourceLabel}&quot; 섹션의 편제 데이터를 이 섹션에 붙여넣습니다.
            </p>
            <p className="page-desc">
              이미 편제 데이터가 있는 경우 기존 데이터는 덮어쓰기 됩니다. 붙여넣은 뒤 오른쪽 위
              &quot;저장하기&quot;를 눌러야 서버에 반영됩니다.
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setPasteTargetSectionId(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={applyCopiedSectionDataToTarget}
              >
                붙여넣기
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {batchLearningConfirmTargets ? (
        <TrackLearningBatchGenerateConfirmModal
          targets={batchLearningConfirmTargets}
          busy={batchLearningBusy}
          onCancel={() => {
            if (batchLearningBusy) return;
            setBatchLearningConfirmTargets(null);
          }}
          onConfirm={() => void handleBatchGenerateLearning()}
        />
      ) : null}

      {batchLearningResult ? (
        <TrackLearningBatchGenerateResultModal
          result={batchLearningResult}
          onClose={() => setBatchLearningResult(null)}
        />
      ) : null}

      {pendingDeleteGroup && (
        <div
          className="cp-modal-backdrop"
          onClick={() => setPendingDeleteGroup(null)}
        >
          <div
            className="cp-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>편제 삭제</h3>
            <p className="page-desc">
              &quot;{pendingDeleteGroup.group[0]?.name}&quot; 편제를 삭제하면 이 클래스에서 해당 편제의
              시간표명, 편제 프로그램, 연결된 진행 데이터가 함께 삭제됩니다. 저장하기를 누르면 DB에 반영됩니다.
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setPendingDeleteGroup(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  if (!pendingDeleteGroup) return;
                  const { sectionId, group } = pendingDeleteGroup;
                  setPendingTrackGroupDeletes((prev) => [...prev, { sectionId, group }]);
                  setDraftEdits((prev) => {
                    const next = { ...prev };
                    group.forEach((t) => {
                      delete next[`${sectionId}-${t.id}`];
                    });
                    return next;
                  });
                  setCriteriaHoursByKey((prev) => {
                    const next = { ...prev };
                    group.forEach((t) => {
                      delete next[`${sectionId}-${t.id}`];
                    });
                    return next;
                  });
                  const deletedName = group[0]?.name;
                  const deletedIds = new Set(group.map((t) => t.id));
                  if (deletedName) {
                    setSectionColumnOrder((prev) => ({
                      ...prev,
                      [sectionId]: (prev[sectionId] ?? []).filter(
                        (id) => id !== deletedName && !deletedIds.has(id)
                      ),
                    }));
                  }
                  setPendingDeleteGroup(null);
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className="cp-fab-save"
        onClick={handleSaveAll}
        disabled={loading || batchLearningBusy || !isDirty || grades.length === 0}
        aria-label="저장하기"
        title="저장하기"
      >
        <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
      </button>

      <AutoStatusToast state={saveToastState} />

      {toastMessage && (
        <div className="cp-toast-overlay" onClick={() => setToastMessage(null)}>
          <div className="cp-toast-box" onClick={(e) => e.stopPropagation()}>
            <div className="cp-toast-message">{toastMessage}</div>
            <button
              type="button"
              className="cp-toast-confirm"
              onClick={() => setToastMessage(null)}
            >
              확인
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default GradeTrackPage;

interface SortableTrackColumnProps {
  sortableId: string;
  group: Track[];
  criteriaHours: string;
  wildcardChar: string;
  wildcardDuplicate: boolean;
  onCriteriaHoursChange: (value: string) => void;
  onWildcardChange: (value: string) => void;
  draftEdit: { name: string; aliases: string[]; wildcardChar?: string } | undefined;
  onSaveDraft: (name: string, aliases: string[]) => void;
  onAliasesReorder: (aliases: string[]) => void;
  onRequestDelete: (group: Track[]) => void;
  programMode: TrackProgramMode;
  onProgramModeChange: (next: TrackProgramMode) => void;
  syncRepAlias: string;
  onSyncRepAliasChange: (alias: string) => void;
  trackCategory: TrackCategory;
  onTrackCategoryToggle: () => void;
  onShowWarning: (message: string) => void;
  learningPublisher: string;
  onLearningPublisherChange: (publisher: string) => void;
  onPublisherTemplateSelect?: (templateName: string) => void;
  termScope: TrackHoursTermScope;
  onTermScopeChange: (scope: TrackHoursTermScope) => void;
  classTitle: string;
  learningGradeNumbers: Array<1 | 2 | 3 | 4 | 5 | 6>;
  workspaceId: string;
  /** 저장된 시간표명 순서(없으면 group/DB 순서 fallback) */
  orderedAliases?: string[];
}

const SortableTrackColumn: React.FC<SortableTrackColumnProps> = ({
  sortableId,
  group,
  criteriaHours,
  wildcardChar,
  wildcardDuplicate,
  onCriteriaHoursChange,
  onWildcardChange,
  draftEdit,
  onSaveDraft,
  onAliasesReorder,
  onRequestDelete,
  programMode,
  onProgramModeChange,
  syncRepAlias,
  onSyncRepAliasChange,
  trackCategory,
  onTrackCategoryToggle,
  onShowWarning,
  learningPublisher,
  onLearningPublisherChange,
  onPublisherTemplateSelect,
  termScope,
  onTermScopeChange,
  classTitle,
  learningGradeNumbers,
  workspaceId,
  orderedAliases,
}) => {
  const name = group[0]?.name ?? '';
  const currentAliasCandidates = group.map((t) => (t.description ?? '').trim());
  const nonEmptyCurrentAliases = currentAliasCandidates.filter((a) => a.length > 0);
  const baseAliases = nonEmptyCurrentAliases.length > 0 ? nonEmptyCurrentAliases : [name];
  const displayName = draftEdit?.name?.trim() ?? name;
  /**
   * draft가 없으면 DB tracks 배열 순서가 아니라, 저장된 시간표명 순서(orderedAliases)를 쓴다.
   * getTracks는 name 정렬만 해서 동일 편제명 안 순서가 불안정하고, 저장 직후 일부 편제만 회귀해 보이는 원인이 된다.
   */
  const displayAliases = draftEdit?.aliases?.length
    ? draftEdit.aliases
    : orderedAliases && orderedAliases.length > 0
      ? orderedAliases
      : baseAliases;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(displayName);
  const [draftAliases, setDraftAliases] = useState(displayAliases);
  const [draftAliasIds, setDraftAliasIds] = useState<string[]>(() =>
    displayAliases.map(() => generateId())
  );
  const aliasDragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  const handleCriteriaHoursChangeLocal = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 3);
    onCriteriaHoursChange(digitsOnly);
  };
  const handleSave = () => {
    const nextName = draftName.trim();
    if (!nextName) return;

    const trimmedAliases = draftAliases.map((a) => a.trim());
    const nonEmptyCount = trimmedAliases.filter((a) => a.length > 0).length;
    if (nonEmptyCount === 0) {
      onShowWarning('시간표명은 최소 1개 이상 입력해야 합니다.');
      return;
    }

    onSaveDraft(nextName, trimmedAliases.filter((a) => a.length > 0));
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={'track-col' + (isDragging ? ' dragging' : '')}
      {...attributes}
      {...listeners}
    >
      <div className="track-col-header">
        <div className="track-header-top">
          <div className="track-header-actions">
            <button
              type="button"
              className="track-icon-button"
              onClick={() => {
                setDraftName(displayName);
                setDraftAliases(displayAliases);
                setDraftAliasIds(displayAliases.map(() => generateId()));
                if (!editing && !sanitizeWildcardChar(wildcardChar)) {
                  const def = defaultWildcardFromName(displayName);
                  if (def) onWildcardChange(def);
                }
                setEditing((prev) => !prev);
              }}
            >
              <img src="/edit.svg" alt="편제 수정" className="track-icon" />
            </button>
            <button
              type="button"
              className="track-icon-button"
              onClick={() => onRequestDelete(group)}
            >
              <img src="/Trash.svg" alt="편제 삭제" className="track-icon" />
            </button>
          </div>
        </div>
        <div className="track-category-toggle-row">
          <button
            type="button"
            className={'track-category-toggle-badge' + (trackCategory === 'other' ? ' is-other' : '')}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onTrackCategoryToggle}
            aria-label={`편제 속성: ${trackCategory === 'other' ? '기타' : '정규'} (클릭 시 ${
              trackCategory === 'other' ? '정규' : '기타'
            }로 변경)`}
            title="클릭하여 편제 속성을 정규/기타로 변경"
          >
            {trackCategory === 'other' ? '기타' : '정규'}
          </button>
        </div>
        <div className="track-col-name">
          {editing ? (
            <input
              className="track-name-inline"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
            />
          ) : (
            displayName
          )}
        </div>
      </div>
      <TrackWildcardInput
        value={wildcardChar}
        duplicate={wildcardDuplicate}
        onChange={onWildcardChange}
      />
      <div className="track-col-body">
        {editing
          ? (() => {
              const items = draftAliasIds;
              const handleAliasDragEnd = (event: DragEndEvent) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const oldIndex = draftAliasIds.indexOf(String(active.id));
                const newIndex = draftAliasIds.indexOf(String(over.id));
                if (oldIndex < 0 || newIndex < 0) return;
                setDraftAliasIds((prev) => arrayMove(prev, oldIndex, newIndex));
                setDraftAliases((prev) => {
                  const next = arrayMove(prev, oldIndex, newIndex);
                  onAliasesReorder(next);
                  return next;
                });
              };
              return (
                <DndContext
                  sensors={aliasDragSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleAliasDragEnd}
                >
                  <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    {draftAliasIds.map((aliasId, index) => (
                      <SortableAliasInlineRow
                        key={aliasId}
                        id={aliasId}
                        alias={draftAliases[index] ?? ''}
                        onAliasChange={(value) =>
                          setDraftAliases((prev) => {
                            const next = [...prev];
                            next[index] = value;
                            return next;
                          })
                        }
                        removable={draftAliases.length >= 2}
                        onRemove={() => {
                          setDraftAliases((prev) => prev.filter((_, i) => i !== index));
                          setDraftAliasIds((prev) => prev.filter((_, i) => i !== index));
                        }}
                      />
                    ))}
                  </SortableContext>
                  <button
                    type="button"
                    className="track-alias-add-btn"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      setDraftAliases((prev) => [...prev, '']);
                      setDraftAliasIds((prev) => [...prev, generateId()]);
                    }}
                    aria-label="시간표명 추가"
                    title="시간표명 추가"
                  >
                    + 시간표명
                  </button>
                </DndContext>
              );
            })()
          : displayAliases.map((alias, index) =>
              programMode === 'sync' ? (
                <button
                  key={`${name}-${index}`}
                  type="button"
                  className={
                    'track-alias track-alias-sync-select' +
                    (alias === syncRepAlias ? ' is-sync-rep' : ' is-sync-non-rep')
                  }
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => onSyncRepAliasChange(alias)}
                  title={
                    alias === syncRepAlias
                      ? '대표 시간표(프로그램 편집은 대표 시간표 메뉴에서만 가능하며 저장 시 다른 동기화 프로그램으로 복제됩니다)'
                      : '클릭하여 대표 시간표로 지정 (대표에서 저장한 내용이 다른 동기화 프로그램으로 복제됩니다)'
                  }
                >
                  <span className="track-alias-sync-select-label">{alias}</span>
                  {alias === syncRepAlias ? (
                    <span className="cp-class-representative-badge track-alias-rep-badge">대표</span>
                  ) : null}
                  <TrackAliasDefaultBadge
                    visible={index === 0 && !!sanitizeWildcardChar(wildcardChar)}
                  />
                </button>
              ) : (
                <div key={`${name}-${index}`} className="track-alias">
                  {alias}
                  <TrackAliasDefaultBadge
                    visible={index === 0 && !!sanitizeWildcardChar(wildcardChar)}
                  />
                </div>
              )
            )}
        {displayAliases.length >= 2 && (
          <div className="track-split-mode-row">
            <span className="track-split-mode-title">프로그램</span>
            <span className="track-split-mode-title">생성 방법</span>
            <button
              type="button"
              className={`track-split-single-toggle-btn ${
                programMode !== 'unified' ? 'is-on' : 'is-off'
              } ${programMode === 'sync' ? 'is-sync' : ''}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onProgramModeChange(getNextProgramMode(programMode))}
              aria-label={`프로그램 생성 방법: ${getProgramModeLabel(programMode)} (클릭 시 ${
                getProgramModeLabel(getNextProgramMode(programMode))
              })`}
              title="클릭할 때마다 편제별 → 시간표별 → 동기화 순서로 전환"
            >
              {getProgramModeLabel(programMode)}
            </button>
          </div>
        )}
      </div>
      <div
        className="track-col-criteria"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="track-col-criteria-label">기준시수</span>
        <NumericInput
          className="track-col-criteria-input"
          min={0}
          max={999}
          maxLength={3}
          selectAllOnFocus
          value={criteriaHours}
          onChange={handleCriteriaHoursChangeLocal}
        />
      </div>
      <TrackLearningPublisherSelect
        workspaceId={workspaceId}
        classTitle={classTitle}
        gradeNumbers={learningGradeNumbers}
        trackName={displayName}
        value={learningPublisher}
        onChange={onLearningPublisherChange}
        onTemplateSelect={onPublisherTemplateSelect}
      />
      <TrackHoursTermScopeToggle value={termScope} onChange={onTermScopeChange} />
      {editing && (
        <div className="track-edit-footer">
          <button
            type="button"
            className="track-edit-cancel"
            onClick={() => setEditing(false)}
          >
            닫기
          </button>
          <button
            type="button"
            className="track-edit-save"
            onClick={handleSave}
          >
            확인
          </button>
        </div>
      )}
    </div>
  );
};
